import React, { useState, useEffect } from 'react';
import { API, downloadAuthed, openAuthed } from '@/lib/axios';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PlusCircle, Trash2, AlertTriangle, CheckCircle, FileText, Loader2, Sparkles, RefreshCw, Edit, Eye, Printer, WifiOff, Wifi, PenLine, UserPlus } from 'lucide-react';
import { useOffline } from '@/contexts/OfflineContext';
import { useTranslation } from 'react-i18next';
import { getOfflineJSAs } from '@/services/syncService';
import { saveBulkToStore, saveToStore, addToSyncQueue } from '@/services/offlineDB';
import SignaturePad from '@/components/SignaturePad';
import JsaSourceBadges from '@/components/JsaSourceBadges';
import { rowId, withRowIds, rowKey, stripRowIds } from '@/lib/rowKeys';

const JSAManager = ({ user, divisionIdOverride = null }) => {
  const { t } = useTranslation();
  // Use divisionIdOverride if provided (from URL), otherwise fall back to user's divisionId
  const effectiveDivisionId = divisionIdOverride || user?.divisionId || null;
  
  // Offline support
  const { isOnline, updatePendingCount } = useOffline();
  const [isOfflineData, setIsOfflineData] = useState(false);
  
  const [jsas, setJsas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedJsa, setSelectedJsa] = useState(null);
  const [workers, setWorkers] = useState([]);
  
  // JSA Update Queue state (for supervisors to approve AI suggestions)
  const [updateQueue, setUpdateQueue] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [showUpdateQueue, setShowUpdateQueue] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    taskDescription: '',
    toolsBeingUsed: '',
    location: '',
    emergencyEquipmentLocation: '',
    date: new Date().toISOString().split('T')[0],
    hazards: [],
    assignedWorkerIds: [],
    notes: '',
    linkedSdsDocuments: [],
    sitePhotos: [],
    materials: '',
    conditions: '',
    sourceJhaId: ''
  });

  // Source-first draft generation (Luna organizes company records, Sol develops gaps)
  const [draftJobId, setDraftJobId] = useState(null);
  const [draftProgress, setDraftProgress] = useState('');
  const [draftError, setDraftError] = useState('');
  const [draftSources, setDraftSources] = useState(null);
  const [draftStages, setDraftStages] = useState(null);
  const [jhaCandidates, setJhaCandidates] = useState([]);
  
  // Site Photo Analysis state
  const [sitePhotoPreview, setSitePhotoPreview] = useState(null);
  const [analyzingPhoto, setAnalyzingPhoto] = useState(false);
  const [photoAnalysisResult, setPhotoAnalysisResult] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = React.useRef(null);
  
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [workerSearchQuery, setWorkerSearchQuery] = useState('');
  const [workerSearch, setWorkerSearch] = useState('');
  const [showWorkerDropdown, setShowWorkerDropdown] = useState(false);
  
  // Filtered workers for edit dialog
  const filteredWorkers = workers.filter(worker => 
    worker.name.toLowerCase().includes(workerSearch.toLowerCase()) &&
    !selectedWorkers.find(sw => sw.id === worker.id)
  );
  
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [newHazard, setNewHazard] = useState({ hazard: '', mitigation: '', ppe: '' });
  const [generatingMitigation, setGeneratingMitigation] = useState(false);
  const [hazardDebounceTimer, setHazardDebounceTimer] = useState(null);
  
  // SDS PPE Recommendations state
  const [fetchingSdsPpe, setFetchingSdsPpe] = useState(false);
  const [sdsPpeRecommendations, setSdsPpeRecommendations] = useState([]);
  const [matchedSdsDocuments, setMatchedSdsDocuments] = useState([]);
  const [selectedSdsIds, setSelectedSdsIds] = useState(new Set()); // Track selected SDS for attachment
  
  // Task Template state
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [showTemplatesDialog, setShowTemplatesDialog] = useState(false);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  
  // JHA Import state (Import from Pre-Construction JHAs)
  const [showJhaImportDialog, setShowJhaImportDialog] = useState(false);
  const [jhaImportTasks, setJhaImportTasks] = useState([]);
  const [jhaImportSearch, setJhaImportSearch] = useState('');
  const [loadingJhaImport, setLoadingJhaImport] = useState(false);
  
  // Related Audits state (for JSA-Audit linkage visualization)
  const [relatedAudits, setRelatedAudits] = useState([]);
  const [loadingRelatedAudits, setLoadingRelatedAudits] = useState(false);
  
  // Change History state
  const [changeHistory, setChangeHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistoryTab, setShowHistoryTab] = useState(false);
  
  // Training Status state (for JSA Training Verification)
  const [trainingStatus, setTrainingStatus] = useState(null);
  const [loadingTrainingStatus, setLoadingTrainingStatus] = useState(false);

  // On-device Co-Sign state (workers who don't have their phone)
  const [showCoSignDialog, setShowCoSignDialog] = useState(false);
  const [coSignTarget, setCoSignTarget] = useState(null); // { type: 'registered'|'manual', workerId?, workerName? }
  const [coSignPrintedName, setCoSignPrintedName] = useState('');
  const [coSignSignature, setCoSignSignature] = useState(null);
  const [savingCoSign, setSavingCoSign] = useState(false);

  // Submit-to-Admin dialog (requires submitter's own signature + printed name)
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitPrintedName, setSubmitPrintedName] = useState('');
  const [submitSignature, setSubmitSignature] = useState(null);
  const [submittingToAdmin, setSubmittingToAdmin] = useState(false);

  useEffect(() => {
    fetchJSAs();
    fetchWorkers();
    fetchTaskTemplates();
    
    // Auto-refresh JSAs every 10 seconds to show signature updates (only when online)
    const refreshInterval = setInterval(() => {
      if (isOnline) {
        fetchJSAs(true); // Pass true to indicate background refresh
      }
    }, 10000);
    
    return () => clearInterval(refreshInterval);
  }, [isOnline]);

  const fetchJSAs = async (isBackgroundRefresh = false) => {
    try {
      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      
      // Try online first, fall back to offline
      if (isOnline) {
        // Use effectiveDivisionId for filtering
        const params = effectiveDivisionId ? `?divisionId=${effectiveDivisionId}` : '';
        const response = await axiosInstance.get(`/jsa${params}`);
        setJsas(response.data);
        setIsOfflineData(false);
        
        // Cache JSAs for offline use
        if (response.data && response.data.length > 0) {
          await saveBulkToStore('jsas', response.data);
          console.log('📦 Cached', response.data.length, 'JSAs for offline');
        }
      } else {
        // Offline mode - load from cache
        await loadOfflineJsas();
      }
    } catch (error) {
      console.error('Error fetching JSAs:', error);
      if (!isBackgroundRefresh) {
        // Try offline fallback
        await loadOfflineJsas();
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const loadOfflineJsas = async () => {
    try {
      const offlineJsas = await getOfflineJSAs();
      if (offlineJsas && offlineJsas.length > 0) {
        // Filter by division if needed
        const filtered = effectiveDivisionId 
          ? offlineJsas.filter(j => j.divisionId === effectiveDivisionId || !j.divisionId)
          : offlineJsas;
        setJsas(filtered);
        setIsOfflineData(true);
        toast.info('📴 Showing cached JSAs (offline mode)', { duration: 3000 });
      } else {
        setJsas([]);
        toast.warning('No cached JSAs available. Connect to internet to download.', { duration: 4000 });
      }
    } catch (error) {
      console.error('Failed to load offline JSAs:', error);
      setJsas([]);
    }
  };

  const fetchWorkers = async () => {
    try {
      // Use effectiveDivisionId for filtering workers
      const params = effectiveDivisionId ? `?divisionId=${effectiveDivisionId}` : '';
      console.log('Fetching workers with params:', params);
      const response = await axiosInstance.get(`/admin/users${params}`);
      console.log('Users response:', response.data?.length, 'users');
      const workerList = response.data.filter(u => u.role === 'worker' && u.isActive);
      console.log('Filtered workers:', workerList.length);
      setWorkers(workerList);
      
      if (workerList.length === 0 && response.data?.length > 0) {
        console.log('No workers found. Available roles:', [...new Set(response.data.map(u => u.role))]);
      }
    } catch (error) {
      console.error('Error fetching workers:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load workers: ' + (error.response?.data?.detail || error.message));
    }
  };

  const fetchTaskTemplates = async () => {
    try {
      const response = await axiosInstance.get('/task-templates');
      setTaskTemplates(response.data);
    } catch (error) {
      console.error('Error fetching task templates:', error);
    }
  };

  const fetchRelatedAudits = async (jsaId) => {
    setLoadingRelatedAudits(true);
    try {
      const response = await axiosInstance.get(`/jsa/${jsaId}/related-audits`);
      setRelatedAudits(response.data.audits || []);
    } catch (error) {
      console.error('Error fetching related audits:', error);
      setRelatedAudits([]);
    } finally {
      setLoadingRelatedAudits(false);
    }
  };
  
  // Fetch training status for workers assigned to a JSA
  const fetchTrainingStatus = async (jsaId) => {
    setLoadingTrainingStatus(true);
    try {
      const response = await axiosInstance.get(`/jsa/${jsaId}/training-status`);
      setTrainingStatus(response.data);
    } catch (error) {
      console.error('Error fetching training status:', error);
      setTrainingStatus(null);
    } finally {
      setLoadingTrainingStatus(false);
    }
  };

  const fetchChangeHistory = async (jsaId) => {
    setLoadingHistory(true);
    try {
      const response = await axiosInstance.get(`/jsa/${jsaId}/change-history`);
      setChangeHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching change history:', error);
      setChangeHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Template name is required');
      return;
    }

    if (formData.hazards.length === 0) {
      toast.error('Add at least one hazard to save as template');
      return;
    }

    try {
      await axiosInstance.post('/task-templates', {
        taskName: templateName,
        description: templateDescription,
        hazards: formData.hazards.map(({ _rowId, ...h }) => h)
      });
      toast.success('Template saved successfully');
      setShowSaveTemplateDialog(false);
      setTemplateName('');
      setTemplateDescription('');
      fetchTaskTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const handleLoadTemplate = async (templateId) => {
    try {
      const response = await axiosInstance.get(`/task-templates/${templateId}`);
      const template = response.data;
      
      setFormData(prev => ({
        ...prev,
        taskDescription: template.taskName,
        hazards: withRowIds(template.hazards || [])
      }));
      
      setShowTemplatesDialog(false);
      toast.success(`Template "${template.taskName}" loaded`);
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Failed to load template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this task template?')) return;

    try {
      await axiosInstance.delete(`/task-templates/${templateId}`);
      toast.success('Template deleted');
      fetchTaskTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  // JHA Import Functions
  const fetchJhaImportTasks = async (search = '') => {
    setLoadingJhaImport(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await axiosInstance.get(`/jsa/import-from-jha${params}`);
      setJhaImportTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching JHA tasks:', error);
      toast.error('Failed to load JHA library');
    } finally {
      setLoadingJhaImport(false);
    }
  };

  const handleImportFromJha = (task) => {
    // Import task description
    setFormData(prev => ({
      ...prev,
      taskDescription: task.task,
      hazards: [
        ...prev.hazards,
        ...task.hazards.map(h => ({
          _rowId: rowId(),
          hazard: h.hazard,
          mitigation: h.mitigation,
          ppe: h.ppe
        }))
      ]
    }));
    
    toast.success(`Imported "${task.task}" with ${task.hazards.length} hazards from JHA`);
    setShowJhaImportDialog(false);
  };

  const handleToolsChange = async (toolsText) => {
    // Update tools field
    setFormData({ ...formData, toolsBeingUsed: toolsText });
    
    // Auto-populate PPE for hazards if tools are entered
    if (toolsText.trim()) {
      try {
        const response = await axiosInstance.post('/tools/get-ppe', {
          tools: toolsText
        });
        
        const recommendedPPE = response.data.recommended_ppe;
        
        // Update PPE in all hazards that don't have PPE yet
        const updatedHazards = formData.hazards.map(h => ({
          ...h,
          ppe: h.ppe || recommendedPPE
        }));
        
        setFormData(prev => ({ ...prev, hazards: updatedHazards }));
        
        // Also update the new hazard form if empty
        if (!newHazard.ppe) {
          setNewHazard(prev => ({ ...prev, ppe: recommendedPPE }));
        }
        
        toast.success(`PPE requirements auto-populated based on tools`);
      } catch (error) {
        console.error('Failed to get PPE recommendations:', error);
      }
    }
  };

  const handleFetchSdsPpe = async () => {
    const toolsText = formData.toolsBeingUsed.trim();
    
    if (!toolsText) {
      toast.error('Please enter tools or chemicals first');
      return;
    }

    setFetchingSdsPpe(true);
    try {
      const response = await axiosInstance.post('/jsa/sds-ppe-recommendations', {
        items: toolsText
      });

      const { ppe_recommendations, matched_sds, total_matched, message } = response.data;
      
      setSdsPpeRecommendations(ppe_recommendations || []);
      setMatchedSdsDocuments(matched_sds || []);
      
      // Pre-select all matched SDS by default, but let user uncheck
      if (matched_sds && matched_sds.length > 0) {
        const allIds = new Set(matched_sds.map(sds => sds.sds_id));
        setSelectedSdsIds(allIds);
      } else {
        setSelectedSdsIds(new Set());
      }

      if (total_matched > 0) {
        toast.success(`Found ${total_matched} SDS document(s) - Select which to attach`);
      } else {
        toast.info('No matching SDS documents found in library');
      }
    } catch (error) {
      console.error('Error fetching SDS PPE recommendations:', error);
      toast.error('Failed to fetch PPE recommendations from SDS library');
    } finally {
      setFetchingSdsPpe(false);
    }
  };
  
  // Toggle SDS selection
  const toggleSdsSelection = (sdsId) => {
    setSelectedSdsIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sdsId)) {
        newSet.delete(sdsId);
      } else {
        newSet.add(sdsId);
      }
      return newSet;
    });
  };
  
  // Select/Deselect all SDS
  const toggleAllSds = (selectAll) => {
    if (selectAll) {
      setSelectedSdsIds(new Set(matchedSdsDocuments.map(sds => sds.sds_id)));
    } else {
      setSelectedSdsIds(new Set());
    }
  };
  
  // Apply selected SDS to JSA
  const applySelectedSdsToJsa = () => {
    const selectedSds = matchedSdsDocuments.filter(sds => selectedSdsIds.has(sds.sds_id));
    
    if (selectedSds.length === 0) {
      toast.warning('No SDS selected. Please select at least one SDS to attach.');
      return;
    }
    
    const sdsLinks = selectedSds.map(sds => ({
      sds_id: sds.sds_id,
      chemical_name: sds.chemical_name,
      product_name: sds.product_name,
      concentration: sds.concentration,
      manufacturer: sds.manufacturer || ''
    }));
    
    setFormData(prev => ({
      ...prev,
      linkedSdsDocuments: sdsLinks
    }));
    
    toast.success(`${selectedSds.length} SDS document(s) attached to JSA`);
  };

  const handleApplySdsPpe = () => {
    if (sdsPpeRecommendations.length === 0) {
      toast.error('No PPE recommendations to apply');
      return;
    }

    const ppeString = sdsPpeRecommendations.join(', ');

    // Update PPE in all existing hazards
    const updatedHazards = formData.hazards.map(h => ({
      ...h,
      ppe: h.ppe ? `${h.ppe}, ${ppeString}` : ppeString
    }));

    setFormData(prev => ({ ...prev, hazards: updatedHazards }));

    // Also update the new hazard form
    setNewHazard(prev => ({
      ...prev,
      ppe: prev.ppe ? `${prev.ppe}, ${ppeString}` : ppeString
    }));

    toast.success('PPE recommendations from SDS applied to hazards');
  };

  // ---- Source-first daily JSA draft: background job + recovery after leaving/refreshing ----
  const DRAFT_JOB_KEY = 'jsa_draft_job_id';

  const applyDraftResult = (res) => {
    setFormData(prev => ({ ...prev, hazards: withRowIds(res.hazards || []) }));
    setDraftSources(res.sources || null);
    setDraftStages(res.stages || null);
    if (res.source === 'approved_library_exact') {
      toast.success('📚 Approved library already covers this task — no AI used');
    } else {
      toast.success(res.message || 'Draft ready for your review');
    }
  };

  const pollDraftJob = async (jobId) => {
    setDraftJobId(jobId);
    localStorage.setItem(DRAFT_JOB_KEY, jobId);
    setAutoPopulating(true);
    setDraftError('');
    try {
      for (let i = 0; i < 180; i++) {
        const { data } = await axiosInstance.get(`/ai-jobs/${jobId}`);
        setDraftProgress(data.progress || data.status);
        if (data.status === 'completed') {
          applyDraftResult(data.result || {});
          localStorage.removeItem(DRAFT_JOB_KEY);
          localStorage.removeItem('jsa_draft_job_payload');
          return;
        }
        if (data.status === 'failed') {
          setDraftError(data.error || 'Generation failed. Your inputs were kept — tap retry.');
          toast.error(data.error || 'Generation failed. Your inputs were kept — tap retry.');
          localStorage.removeItem(DRAFT_JOB_KEY);
          return;
        }
        await new Promise(r => setTimeout(r, 3000));
      }
      setDraftError('Still generating. Leave this screen if you like — reopen to recover the draft.');
    } catch (error) {
      if (error.response?.status === 404) {
        localStorage.removeItem(DRAFT_JOB_KEY);
      }
      setDraftError(error.response?.data?.detail || error.message || 'Could not read the generation job');
    } finally {
      setAutoPopulating(false);
      setDraftProgress('');
    }
  };

  // Recover a job that was still running when the user left or refreshed
  useEffect(() => {
    const saved = localStorage.getItem(DRAFT_JOB_KEY);
    if (saved && showCreateDialog) {
      try {
        const kept = JSON.parse(localStorage.getItem('jsa_draft_job_payload') || 'null');
        if (kept) {
          setFormData(prev => ({
            ...prev,
            taskDescription: prev.taskDescription || kept.taskDescription || '',
            location: prev.location || kept.location || '',
            toolsBeingUsed: prev.toolsBeingUsed || kept.toolsUsed || '',
            materials: prev.materials || kept.materials || '',
            conditions: prev.conditions || kept.conditions || '',
            sourceJhaId: prev.sourceJhaId || kept.sourceJhaId || '',
          }));
        }
      } catch (e) { /* ignore */ }
      toast.info('Recovering your JSA draft that was still generating…');
      pollDraftJob(saved);
    }
  }, [showCreateDialog]);

  const fetchJhaCandidates = async () => {
    const task = formData.taskDescription.trim();
    if (!task) return;
    try {
      const { data } = await axiosInstance.get('/jsa-source-jhas', {
        params: { task, location: formData.location || '', projectName: '' }
      });
      setJhaCandidates(data.candidates || []);
    } catch (e) {
      setJhaCandidates([]);
    }
  };

  const handleAutoPopulate = async () => {
    const taskInfo = formData.taskDescription.trim();
    if (!taskInfo) {
      toast.error('Please enter a task description first');
      return;
    }

    setDraftSources(null);
    setDraftStages(null);
    setDraftError('');
    setAutoPopulating(true);
    setDraftProgress('Searching your JHAs, library, manual and SOPs');
    try {
      const { data } = await axiosInstance.post('/jsa/auto-populate-async', {
        taskDescription: taskInfo,
        location: formData.location || '',
        toolsUsed: formData.toolsBeingUsed || '',
        materials: formData.materials || '',
        conditions: formData.conditions || '',
        sourceJhaId: formData.sourceJhaId || null,
      });
      localStorage.setItem('jsa_draft_job_payload', JSON.stringify({
        taskDescription: taskInfo,
        location: formData.location || '',
        toolsUsed: formData.toolsBeingUsed || '',
        materials: formData.materials || '',
        conditions: formData.conditions || '',
        sourceJhaId: formData.sourceJhaId || '',
      }));
      if (data.reused) toast.info('Already generating this task — reconnecting to that job');
      await pollDraftJob(data.jobId);
    } catch (error) {
      console.error('Error starting JSA draft:', error);
      const msg = error.response?.data?.detail || error.message || 'Failed to start generation';
      setDraftError(msg);
      toast.error(`Auto-populate failed: ${msg}`);
      setAutoPopulating(false);
      setDraftProgress('');
    }
  };


  const handleGenerateMitigation = async (hazardText) => {
    if (!hazardText || hazardText.trim().length < 5) {
      return; // Wait for more meaningful input
    }

    setGeneratingMitigation(true);
    try {
      const response = await axiosInstance.post('/jsa/hazard-mitigation', {
        hazardDescription: hazardText,
        taskDescription: formData.taskDescription || '',  // Include task context
        toolsUsed: formData.toolsBeingUsed || ''  // Include tools context
      });

      const { source, mitigations } = response.data;
      
      if (mitigations && mitigations.length > 0) {
        setNewHazard(prev => ({
          ...prev,
          mitigation: mitigations[0] // Use first suggestion
        }));

        if (source === 'predefined' || source === 'database') {
          toast.success('✓ Mitigation auto-populated');
        } else if (source === 'ai_generated') {
          toast.success('✨ AI mitigation based on task context');
        } else if (source === 'company_safety_manual') {
          toast.success('📋 Mitigation from company safety manual');
        }
      }
    } catch (error) {
      console.error('Error generating mitigation:', error);
      // Silent fail - user can enter manually
    } finally {
      setGeneratingMitigation(false);
    }
  };

  // Auto-populate mitigation when hazard is typed
  const handleHazardChange = (value) => {
    setNewHazard(prev => ({ ...prev, hazard: value }));
    
    // Clear existing timer
    if (hazardDebounceTimer) {
      clearTimeout(hazardDebounceTimer);
    }
    
    // Set new timer to auto-populate after user stops typing
    const timer = setTimeout(() => {
      handleGenerateMitigation(value);
    }, 1500); // Wait 1.5 seconds after user stops typing
    
    setHazardDebounceTimer(timer);
  };

  const handleAddHazard = () => {
    if (!newHazard.hazard.trim() || !newHazard.mitigation.trim()) {
      toast.error('Both hazard and mitigation are required');
      return;
    }

    setFormData(prev => ({
      ...prev,
      hazards: [...prev.hazards, { ...newHazard, _rowId: rowId() }]
    }));

    setNewHazard({ hazard: '', mitigation: '', ppe: '' });
  };

  const handleRemoveHazard = (index) => {
    setFormData(prev => ({
      ...prev,
      hazards: prev.hazards.filter((_, i) => i !== index)
    }));
  };

  const handleEditHazard = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      hazards: prev.hazards.map((h, i) => i === index ? { ...h, [field]: value } : h)
    }));
  };

  // ===== SITE PHOTO ANALYSIS =====
  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64Full = event.target.result;
      const base64Data = base64Full.split(',')[1];
      setSitePhotoPreview(base64Full);
      analyzePhoto(base64Data);
      uploadPhotoToServer(base64Data, file.name);
    };
    reader.readAsDataURL(file);
  };

  const analyzePhoto = async (base64Data) => {
    setAnalyzingPhoto(true);
    setPhotoAnalysisResult(null);
    try {
      const response = await axiosInstance.post('/jsa/analyze-photo', {
        image_base64: base64Data,
        taskDescription: formData.taskDescription || '',
        location: formData.location || ''
      });
      setPhotoAnalysisResult(response.data);
      
      // Auto-fill tools and task description if fields are empty
      if (response.data.toolsDetected && !formData.toolsBeingUsed) {
        setFormData(prev => ({ ...prev, toolsBeingUsed: response.data.toolsDetected }));
      }
      if (response.data.suggestedTask && !formData.taskDescription) {
        setFormData(prev => ({ ...prev, taskDescription: response.data.suggestedTask }));
      }
      
      toast.success(`Found ${response.data.hazards?.length || 0} hazards in photo`);
    } catch (error) {
      console.error('Photo analysis failed:', error);
      toast.error('Photo analysis failed. You can still add hazards manually.');
    } finally {
      setAnalyzingPhoto(false);
    }
  };

  const uploadPhotoToServer = async (base64Data, filename) => {
    setUploadingPhoto(true);
    try {
      const response = await axiosInstance.post('/jsa/upload-photo-base64', {
        image_base64: base64Data,
        filename: filename || 'site_photo.jpg'
      });
      setFormData(prev => ({
        ...prev,
        sitePhotos: [...prev.sitePhotos, response.data.photoId]
      }));
    } catch (error) {
      console.error('Photo upload failed:', error);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const addPhotoHazardsToForm = () => {
    if (!photoAnalysisResult?.hazards?.length) return;
    setFormData(prev => ({
      ...prev,
      hazards: [...prev.hazards, ...withRowIds(photoAnalysisResult.hazards)]
    }));
    toast.success(`Added ${photoAnalysisResult.hazards.length} hazards from photo analysis`);
    setPhotoAnalysisResult(null);
  };

  const removePhoto = () => {
    setSitePhotoPreview(null);
    setPhotoAnalysisResult(null);
    setFormData(prev => ({ ...prev, sitePhotos: [] }));
    if (photoInputRef.current) photoInputRef.current.value = '';
  };
  // ===== END SITE PHOTO ANALYSIS =====

  const handleCreateJSA = async () => {
    if (!formData.taskDescription.trim()) {
      toast.error('Task description is required');
      return;
    }

    if (formData.hazards.length === 0) {
      toast.error('At least one hazard is required');
      return;
    }

    // Validate hazards have mitigations (PPE is now optional - companies can exceed OSHA standards)
    const emptyMitigations = formData.hazards.filter(h => !h.mitigation || h.mitigation.trim() === '');
    
    if (emptyMitigations.length > 0) {
      toast.error(`${emptyMitigations.length} hazard(s) missing control/mitigation. All hazards must have controls.`);
      return;
    }
    
    // PPE is optional - users can manually add PPE requirements as needed
    // This allows companies to go above OSHA standards or leave blank if no PPE needed
    
    // Warn about worker assignment but don't block (workers can be assigned later)
    if (selectedWorkers.length === 0) {
      const confirmCreate = window.confirm('No workers assigned to this JSA. Do you want to create it anyway? You can assign workers later.');
      if (!confirmCreate) {
        return;
      }
    }

    // Use selected worker IDs
    // Ensure hazards are properly formatted (string values only).
    // Provenance / AI-origin metadata is carried through so the library writeback keeps sources.
    const cleanedHazards = (formData.hazards || []).map(h => ({
      hazard: String(h.hazard || ''),
      mitigation: h.mitigation ? String(h.mitigation) : null,
      ppe: h.ppe ? String(h.ppe) : null,
      step: h.step ? String(h.step) : '',
      provenance: h.provenance || [],
      aiGenerated: !!h.aiGenerated,
      aiAugmented: !!h.aiAugmented,
      aiModel: h.aiModel || null,
      verbatimFromSource: !!h.verbatimFromSource,
      source: h.source || null,
      sourceDocumentTitle: h.sourceDocumentTitle || null
    })).filter(h => h.hazard.trim()); // Remove empty hazards

    // Ensure linkedSdsDocuments have all string values
    const cleanedSdsDocuments = (formData.linkedSdsDocuments || []).map(sds => {
      const cleaned = {};
      for (const [key, value] of Object.entries(sds)) {
        // Convert all values to strings, skip null/undefined
        if (value !== null && value !== undefined) {
          cleaned[key] = typeof value === 'object' ? JSON.stringify(value) : String(value);
        }
      }
      return cleaned;
    });

    const jsaData = {
      ...formData,
      hazards: cleanedHazards,
      linkedSdsDocuments: cleanedSdsDocuments,
      assignedWorkers: selectedWorkers.map(w => w.id),
      divisionId: effectiveDivisionId,
      sitePhotos: formData.sitePhotos || []
    };

    // DEBUG: Log what's being sent to API
    console.log('Creating JSA with data:', jsaData);
    console.log('Hazards being sent:', jsaData.hazards);
    console.log('First hazard:', jsaData.hazards?.[0]);
    console.log('Linked SDS:', jsaData.linkedSdsDocuments);

    try {
      const createResponse = await axiosInstance.post('/jsa', jsaData);
      toast.success('JSA created successfully');
      setShowCreateDialog(false);
      resetForm();
      await fetchJSAs();

      // Immediately open the newly-created JSA so the foreman can capture
      // crew signatures on this device without an extra Edit step.
      const newJsa = createResponse?.data;
      if (newJsa?.id) {
        // Supervisor accepted this reviewed/edited draft — save it to the approved learning library
        try {
          const { data: wb } = await axiosInstance.post(`/jsa/${newJsa.id}/accept-to-library`);
          if (wb?.saved) {
            toast.success(`Saved to your learning library (rev ${wb.revision})`);
          }
        } catch (e) {
          console.error('Library writeback failed:', e);
        }
        setSelectedJsa(newJsa);
        setShowViewDialog(true);
      }
    } catch (error) {
      console.error('Error creating JSA:', error);
      console.error('Error response:', error.response?.data);
      
      // Handle Pydantic validation errors (array of objects)
      let errorMessage = 'Failed to create JSA';
      const detail = error.response?.data?.detail;
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation error format: [{type, loc, msg, input}]
        const firstError = detail[0];
        errorMessage = firstError.msg || 'Validation error';
        if (firstError.loc) {
          const field = firstError.loc[firstError.loc.length - 1];
          errorMessage = `${field}: ${errorMessage}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleUpdateStatus = async (jsaId, newStatus) => {
    try {
      await axiosInstance.patch(`/jsa/${jsaId}`, { status: newStatus });
      toast.success('JSA status updated');
      fetchJSAs();
    } catch (error) {
      console.error('Error updating JSA:', error);
      toast.error('Failed to update JSA status');
    }
  };

  const handleDeleteJSA = async (jsaId) => {
    if (!window.confirm('Are you sure you want to remove this JSA from your list?')) return;

    try {
      const response = await axiosInstance.delete(`/jsa/${jsaId}`);
      // Check if it was archived vs deleted
      if (response.data?.archived) {
        toast.success('JSA archived and hidden from your list');
      } else {
        toast.success('JSA deleted successfully');
      }
      fetchJSAs();
    } catch (error) {
      console.error('Error deleting JSA:', error);
      toast.error('Failed to delete JSA');
    }
  };

  // JSA Update Queue functions
  const fetchUpdateQueue = async () => {
    setLoadingQueue(true);
    try {
      const response = await axiosInstance.get('/jsa-update-queue?status=pending');
      setUpdateQueue(response.data?.queue || []);
    } catch (error) {
      console.error('Error fetching update queue:', error);
    } finally {
      setLoadingQueue(false);
    }
  };

  const handleApproveUpdate = async (queueId, suggestionIndex) => {
    try {
      console.log('Approving update:', { queueId, suggestionIndex });
      const response = await axiosInstance.post(`/jsa-update-queue/${queueId}/approve/${suggestionIndex}`);
      console.log('Approve response:', response.data);
      toast.success('JSA update approved and applied');
      fetchUpdateQueue();
    } catch (error) {
      console.error('Error approving update:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to approve update');
    }
  };

  const handleRejectUpdate = async (queueId, suggestionIndex) => {
    const reason = window.prompt('Reason for rejection (optional):');
    try {
      await axiosInstance.post(`/jsa-update-queue/${queueId}/reject/${suggestionIndex}?reason=${encodeURIComponent(reason || '')}`);
      toast.success('JSA update rejected');
      fetchUpdateQueue();
    } catch (error) {
      console.error('Error rejecting update:', error);
      toast.error('Failed to reject update');
    }
  };

  // Load update queue on mount
  useEffect(() => {
    if (user?.role === 'supervisor' || user?.role === 'division_admin') {
      fetchUpdateQueue();
    }
  }, [user?.role]);

  const handleOpenEdit = (jsa) => {
    setSelectedJsa(jsa);
    setFormData({
      taskDescription: jsa.taskDescription || '',
      toolsBeingUsed: jsa.toolsBeingUsed || '',
      location: jsa.location || '',
      emergencyEquipmentLocation: jsa.emergencyEquipmentLocation || '',
      date: jsa.date ? jsa.date.split('T')[0] : new Date().toISOString().split('T')[0],
      hazards: withRowIds(jsa.hazards || []),
      assignedWorkerIds: jsa.assignedWorkers || [],
      notes: jsa.notes || '',
      linkedSdsDocuments: jsa.linkedSdsDocuments || []
    });
    
    // Set selected workers
    const workersList = jsa.assignedWorkers?.map(workerId => {
      const worker = workers.find(w => w.id === workerId);
      return worker || { id: workerId, name: 'Unknown Worker' };
    }) || [];
    setSelectedWorkers(workersList);
    
    // Set matched SDS documents for display
    if (jsa.linkedSdsDocuments && jsa.linkedSdsDocuments.length > 0) {
      setMatchedSdsDocuments(jsa.linkedSdsDocuments);
    }
    
    setShowEditDialog(true);
  };

  const handleUpdateJSA = async () => {
    if (!formData.taskDescription.trim()) {
      toast.error('Task description is required');
      return;
    }

    if (formData.hazards.length === 0) {
      toast.error('At least one hazard is required');
      return;
    }

    if (selectedWorkers.length === 0) {
      toast.error('Please assign at least one worker to this JSA');
      return;
    }

    const jsaData = stripRowIds({
      ...formData,
      assignedWorkers: selectedWorkers.map(w => w.id)
    });

    try {
      await axiosInstance.patch(`/jsa/${selectedJsa.id}`, jsaData);
      toast.success('JSA updated successfully');
      setShowEditDialog(false);
      resetForm();
      fetchJSAs();
    } catch (error) {
      console.error('Error updating JSA:', error);
      toast.error('Failed to update JSA');
    }
  };

  const handleSubmitToAdmin = async (jsaId) => {
    // Open the submit dialog to capture the submitter's own printed name + signature
    setSubmitPrintedName(user?.name || '');
    setSubmitSignature(null);
    setShowSubmitDialog(true);
  };

  const confirmSubmitToAdmin = async () => {
    if (!selectedJsa) return;
    if (!submitPrintedName.trim()) {
      toast.error('Please type your printed name');
      return;
    }
    if (!submitSignature) {
      toast.error('Please sign in the signature pad');
      return;
    }
    try {
      setSubmittingToAdmin(true);
      const response = await axiosInstance.post(`/jsa/${selectedJsa.id}/submit-to-admin`, {
        supervisorPrintedName: submitPrintedName.trim(),
        supervisorSignature: submitSignature,
      });
      toast.success(response.data.message || 'JSA submitted to admin successfully');
      setShowSubmitDialog(false);
      setShowViewDialog(false);
      setSubmitPrintedName('');
      setSubmitSignature(null);
      fetchJSAs();
    } catch (error) {
      console.error('Error submitting JSA:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit JSA to admin');
    } finally {
      setSubmittingToAdmin(false);
    }
  };

  // On-device signature capture: worker signs on the supervisor/service worker's device
  const openCoSignForWorker = (workerId, workerName) => {
    setCoSignTarget({ type: 'registered', workerId, workerName });
    setCoSignPrintedName(workerName || '');
    setCoSignSignature(null);
    setShowCoSignDialog(true);
  };

  const openCoSignForManual = () => {
    setCoSignTarget({ type: 'manual' });
    setCoSignPrintedName('');
    setCoSignSignature(null);
    setShowCoSignDialog(true);
  };

  const confirmCoSign = async () => {
    if (!selectedJsa || !coSignTarget) return;
    if (!coSignPrintedName.trim()) {
      toast.error('Printed name is required');
      return;
    }
    if (!coSignSignature) {
      toast.error('Signature is required');
      return;
    }
    try {
      setSavingCoSign(true);
      const payload = {
        printedName: coSignPrintedName.trim(),
        signatureBase64: coSignSignature,
      };
      if (coSignTarget.type === 'registered') {
        payload.workerId = coSignTarget.workerId;
      } else {
        payload.workerName = coSignPrintedName.trim();
      }
      await axiosInstance.post(`/jsa/${selectedJsa.id}/co-sign`, payload);
      toast.success('Signature captured');
      setShowCoSignDialog(false);
      setCoSignTarget(null);
      setCoSignPrintedName('');
      setCoSignSignature(null);
      // Refresh the selected JSA to show new signature
      const updated = await axiosInstance.get(`/jsa/${selectedJsa.id}`);
      setSelectedJsa(updated.data);
      fetchJSAs(true);
    } catch (error) {
      console.error('Error capturing signature:', error);
      toast.error(error.response?.data?.detail || 'Failed to capture signature');
    } finally {
      setSavingCoSign(false);
    }
  };

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await fetchJSAs();
  };

  const handlePrintJSA = () => {
    if (!selectedJsa) return;
    
    // Create a print-friendly version of the JSA
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>JSA - ${selectedJsa.taskDescription}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            line-height: 1.6;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .header h1 {
            margin: 0;
            font-size: 24px;
            color: #333;
          }
          .section {
            margin: 20px 0;
            page-break-inside: avoid;
          }
          .section-title {
            font-weight: bold;
            font-size: 16px;
            color: #444;
            margin-bottom: 8px;
            border-bottom: 1px solid #ddd;
            padding-bottom: 4px;
          }
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin: 15px 0;
          }
          .info-item label {
            font-weight: bold;
            color: #555;
          }
          .hazard-box {
            border: 1px solid #e74c3c;
            background: #fee;
            padding: 12px;
            margin: 10px 0;
            border-radius: 4px;
            page-break-inside: avoid;
          }
          .hazard-title {
            font-weight: bold;
            color: #c0392b;
            margin-bottom: 4px;
          }
          .mitigation {
            color: #2c3e50;
            margin: 4px 0;
          }
          .ppe {
            color: #2980b9;
            margin: 4px 0;
          }
          .signature-section {
            margin-top: 40px;
            page-break-inside: avoid;
          }
          .signature-box {
            border: 1px solid #333;
            padding: 15px;
            margin: 10px 0;
            min-height: 80px;
          }
          .signature-row {
            display: grid;
            grid-template-columns: 2fr 1fr;
            gap: 20px;
            margin: 10px 0;
          }
          .signature-line {
            border-top: 1px solid #333;
            margin-top: 30px;
            padding-top: 5px;
          }
          @media print {
            body { padding: 10px; }
            .no-print { display: none; }
            .section { page-break-inside: avoid; }
          }
          .acknowledgment {
            background: #e8f5e9;
            border: 1px solid #4caf50;
            padding: 10px;
            margin: 5px 0;
            border-radius: 4px;
          }
          .acknowledgment-name {
            font-weight: bold;
            color: #2e7d32;
          }
          .acknowledgment-time {
            font-size: 12px;
            color: #558b2f;
          }
          .print-button {
            display: block;
            margin: 20px auto;
            padding: 10px 20px;
            background: #3498db;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 16px;
          }
          .print-button:hover {
            background: #2980b9;
          }
        </style>
      </head>
      <body>
        <button class="print-button no-print" onclick="window.print()">🖨️ Print This JSA</button>
        
        <div class="header">
          <h1>Job Safety Analysis</h1>
          <p style="margin: 5px 0; font-size: 14px;">Complete and sign before starting work</p>
        </div>

        <div class="section">
          <div class="section-title">Task Description</div>
          <p>${selectedJsa.taskDescription}</p>
        </div>

        <div class="info-grid">
          <div class="info-item">
            <label>Location:</label>
            <p>${selectedJsa.location || 'Not specified'}</p>
          </div>
          <div class="info-item">
            <label>Date:</label>
            <p>${new Date(selectedJsa.date).toLocaleDateString()}</p>
          </div>
        </div>

        ${selectedJsa.emergencyEquipmentLocation ? `
        <div class="section">
          <div class="section-title">Emergency Equipment Location</div>
          <p>${selectedJsa.emergencyEquipmentLocation}</p>
        </div>
        ` : ''}

        ${selectedJsa.toolsBeingUsed ? `
        <div class="section">
          <div class="section-title">Tools Being Used</div>
          <p>${selectedJsa.toolsBeingUsed}</p>
        </div>
        ` : ''}

        <div class="section">
          <div class="section-title">Identified Hazards & Safety Mitigations</div>
          ${selectedJsa.hazards.map((h, index) => `
            <div class="hazard-box">
              <div class="hazard-title">⚠️ Hazard ${index + 1}: ${h.hazard}</div>
              <div class="mitigation"><strong>Mitigation:</strong> ${h.mitigation}</div>
              ${h.ppe ? `<div class="ppe"><strong>🦺 Required PPE:</strong> ${h.ppe}</div>` : ''}
            </div>
          `).join('')}
        </div>

        ${selectedJsa.notes ? `
        <div class="section">
          <div class="section-title">Additional Notes</div>
          <p>${selectedJsa.notes}</p>
        </div>
        ` : ''}

        ${selectedJsa.assignedWorkerNames || (selectedJsa.assignedWorkers && selectedJsa.assignedWorkers.length > 0) ? `
        <div class="section">
          <div class="section-title">Assigned Workers</div>
          <p>${selectedJsa.assignedWorkerNames || selectedJsa.assignedWorkers.join(', ')}</p>
        </div>
        ` : ''}

        ${selectedJsa.workerAcknowledgments && Object.keys(selectedJsa.workerAcknowledgments).length > 0 ? `
        <div class="section">
          <div class="section-title">Digital Signatures (Completed)</div>
          ${Object.entries(selectedJsa.workerAcknowledgments).map(([workerId, ack]) => `
            <div class="acknowledgment">
              <div class="acknowledgment-name">✓ ${ack.signature || 'Worker'}</div>
              <div class="acknowledgment-time">Signed: ${new Date(ack.timestamp).toLocaleString()}</div>
            </div>
          `).join('')}
        </div>
        ` : ''}

        <div class="signature-section">
          <div class="section-title">Worker Signatures</div>
          <p style="font-size: 13px; color: #666; margin-bottom: 15px;">
            By signing below, I acknowledge that I have reviewed this Job Safety Analysis, 
            understand the hazards and safety requirements, and agree to follow all safety procedures.
          </p>
          
          ${[1, 2, 3, 4, 5].map(num => `
            <div class="signature-row">
              <div>
                <div style="font-size: 12px; color: #666;">Worker Name:</div>
                <div class="signature-line">Print Name</div>
              </div>
              <div>
                <div style="font-size: 12px; color: #666;">Date:</div>
                <div class="signature-line">MM/DD/YYYY</div>
              </div>
            </div>
            <div style="margin-top: 5px;">
              <div style="font-size: 12px; color: #666;">Signature:</div>
              <div class="signature-line"></div>
            </div>
          `).join('')}
        </div>

        <div class="section" style="margin-top: 30px; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 15px;">
          <p><strong>Supervisor:</strong> ${user?.name || 'Supervisor'}</p>
          <p><strong>Company:</strong> SafetyMind AI</p>
          <p><strong>Printed:</strong> ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
  };

  const handleRemoveLinkedAudit = async (jsaId, auditId) => {
    if (!window.confirm('Remove this linked audit from the JSA?')) return;

    try {
      await axiosInstance.delete(`/jsa/${jsaId}/linked-audits/${auditId}`);
      toast.success('Linked audit removed successfully');
      
      // Update the selected JSA's linkedAudits
      setSelectedJsa(prev => ({
        ...prev,
        linkedAudits: prev.linkedAudits.filter(a => a.id !== auditId)
      }));
      
      // Refresh JSA list
      fetchJSAs();
    } catch (error) {
      console.error('Error removing linked audit:', error);
      toast.error('Failed to remove linked audit');
    }
  };

  const resetForm = () => {
    setFormData({
      taskDescription: '',
      toolsBeingUsed: '',
      location: '',
      emergencyEquipmentLocation: '',
      date: new Date().toISOString().split('T')[0],
      hazards: [],
      assignedWorkerIds: [],
      notes: '',
      linkedSdsDocuments: [],
      sitePhotos: [],
      materials: '',
      conditions: '',
      sourceJhaId: ''
    });
    setDraftSources(null);
    setDraftStages(null);
    setDraftError('');
    setJhaCandidates([]);
    localStorage.removeItem('jsa_draft_job_id');
    localStorage.removeItem('jsa_draft_job_payload');
    setSelectedWorkers([]);
    setWorkerSearchQuery('');
    setWorkerSearch('');
    setShowWorkerDropdown(false);
    setNewHazard({ hazard: '', mitigation: '', ppe: '' });
    setSdsPpeRecommendations([]);
    setMatchedSdsDocuments([]);
    setSitePhotoPreview(null);
    setPhotoAnalysisResult(null);
    setAnalyzingPhoto(false);
    setUploadingPhoto(false);
    if (photoInputRef.current) photoInputRef.current.value = '';
    if (hazardDebounceTimer) {
      clearTimeout(hazardDebounceTimer);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-500';
      case 'pending_signatures': return 'bg-orange-500';
      case 'submitted': return 'bg-green-500';
      case 'active': return 'bg-green-500';
      case 'completed': return 'bg-blue-500';
      case 'archived': return 'bg-gray-400';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 overflow-x-hidden">
      {/* Offline Mode Banner */}
      {!isOnline && (
        <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-3">
          <WifiOff className="h-5 w-5 text-orange-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-orange-800">Offline Mode</p>
            <p className="text-xs text-orange-600">You're viewing cached data. Some features may be limited until you're back online.</p>
          </div>
        </div>
      )}
      
      {/* Offline Data Indicator */}
      {isOfflineData && isOnline && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
          <Wifi className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-800">Connection Restored</p>
            <p className="text-xs text-blue-600">Refreshing data from server...</p>
          </div>
        </div>
      )}
      
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">{t('jsa.title')}</h2>
          <p className="text-sm sm:text-base text-gray-600">{t('jsa.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Update Queue Button - Only for supervisors */}
          {(user?.role === 'supervisor' || user?.role === 'division_admin') && updateQueue.length > 0 && (
            <Button 
              variant={showUpdateQueue ? "default" : "outline"}
              onClick={() => {
                setShowUpdateQueue(!showUpdateQueue);
                if (!showUpdateQueue) fetchUpdateQueue();
              }}
              className="relative"
            >
              <AlertTriangle className="mr-2 h-4 w-4" />
              Update Queue
              <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {updateQueue.reduce((acc, q) => acc + (q.suggestions?.filter(s => s.status === 'pending').length || 0), 0)}
              </span>
            </Button>
          )}
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleManualRefresh}
            disabled={refreshing}
            title="Refresh JSA list"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" onClick={() => setShowTemplatesDialog(true)}>
            <FileText className="mr-2 h-4 w-4" />
            {t('jsa.myTemplates')} ({taskTemplates.length})
          </Button>
          <Button 
            variant="outline" 
            onClick={() => {
              fetchJhaImportTasks();
              setShowJhaImportDialog(true);
            }}
            className="text-purple-600 hover:text-purple-700 border-purple-200 hover:bg-purple-50"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Import from JHA
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}>
            <PlusCircle className="mr-2 h-4 w-4" />
            {t('jsa.createJsa')}
          </Button>
        </div>
      </div>

      {/* Update Queue Section - for supervisor approval */}
      {showUpdateQueue && (user?.role === 'supervisor' || user?.role === 'division_admin') && (
        <Card className="mb-6 border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              JSA Update Queue - Awaiting Your Approval
            </CardTitle>
            <p className="text-sm text-gray-600">
              AI-suggested improvements based on safety audits. Review and approve to update your JSA library.
            </p>
          </CardHeader>
          <CardContent>
            {loadingQueue ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : updateQueue.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No pending updates</p>
            ) : (
              <div className="space-y-4">
                {updateQueue.map((queueItem) => (
                  <div key={queueItem.id} className="border rounded-lg p-4 bg-white">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-medium">{queueItem.jsaTitle || 'JSA Library Update'}</h4>
                        <p className="text-xs text-gray-500">
                          From audit: {queueItem.auditLocation || 'Safety Audit'} • {new Date(queueItem.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {queueItem.suggestions?.filter(s => s.status === 'pending').map((suggestion, idx) => {
                        // Parse the suggestion_text to show meaningful content
                        const suggestionText = suggestion.suggestion_text || suggestion.description || suggestion.value || '';
                        const parts = suggestionText.split('|').map(p => p.trim());
                        const taskName = parts[0] || 'Update';
                        const changes = parts.slice(1).filter(p => p.length > 0);
                        
                        return (
                        <div key={idx} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          {/* Task/Category Name */}
                          <div className="flex items-center gap-2 mb-3">
                            <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded font-medium">
                              {taskName}
                            </span>
                          </div>
                          
                          {/* Proposed Changes */}
                          {changes.length > 0 ? (
                            <div className="space-y-2 mb-3">
                              {changes.map((change, i) => {
                                const isHazard = change.toLowerCase().includes('hazard');
                                const isControl = change.toLowerCase().includes('control');
                                const isPPE = change.toLowerCase().includes('ppe');
                                
                                return (
                                  <div key={i} className={`text-sm p-2 rounded ${
                                    isHazard ? 'bg-red-50 border-l-4 border-red-400' :
                                    isControl ? 'bg-green-50 border-l-4 border-green-400' :
                                    isPPE ? 'bg-blue-50 border-l-4 border-blue-400' :
                                    'bg-gray-100 border-l-4 border-gray-400'
                                  }`}>
                                    <span className={`font-medium ${
                                      isHazard ? 'text-red-700' :
                                      isControl ? 'text-green-700' :
                                      isPPE ? 'text-blue-700' : 'text-gray-700'
                                    }`}>
                                      {isHazard ? '⚠️ ' : isControl ? '🛡️ ' : isPPE ? '👷 ' : '• '}
                                    </span>
                                    {change}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-600 mb-3">{suggestionText || 'No details available'}</p>
                          )}
                          
                          {/* Approve/Reject Buttons */}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleApproveUpdate(queueItem.id, idx)}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50 border-red-200"
                              onClick={() => handleRejectUpdate(queueItem.id, idx)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      );})}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* JSA List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {jsas.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="p-8 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No JSAs created yet</p>
              <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
                Create Your First JSA
              </Button>
            </CardContent>
          </Card>
        ) : (
          jsas.map((jsa) => (
            <Card key={jsa.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{jsa.taskDescription}</CardTitle>
                  <span className={`text-xs px-2 py-1 rounded text-white ${getStatusColor(jsa.status)}`}>
                    {jsa.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{jsa.location || 'No location'}</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center text-gray-600">
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    {jsa.hazards.length} hazard{jsa.hazards.length !== 1 ? 's' : ''}
                  </div>
                  {jsa.assignedWorkerNames && (
                    <div className="flex items-center text-gray-600">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Workers: {jsa.assignedWorkerNames}
                    </div>
                  )}
                  
                  {/* Signature Status */}
                  {jsa.assignedWorkers && jsa.assignedWorkers.length > 0 && (
                    (() => {
                      const acknowledgedCount = jsa.workerAcknowledgments ? Object.keys(jsa.workerAcknowledgments).filter(
                        id => jsa.workerAcknowledgments[id]?.acknowledged
                      ).length : 0;
                      const totalWorkers = jsa.assignedWorkers.length;
                      
                      return (
                        <div className={`flex items-center text-sm ${acknowledgedCount === totalWorkers ? 'text-green-600' : 'text-orange-600'}`}>
                          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {acknowledgedCount}/{totalWorkers} signed
                        </div>
                      );
                    })()
                  )}
                  
                  <p className="text-xs text-gray-500">Date: {new Date(jsa.date).toLocaleDateString()}</p>
                  <p className="text-xs text-gray-500">Created: {new Date(jsa.createdAt).toLocaleDateString()}</p>
                </div>
                
                <div className="mt-4 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedJsa(jsa);
                      setShowViewDialog(true);
                      fetchRelatedAudits(jsa.id);
                      fetchChangeHistory(jsa.id);
                      fetchTrainingStatus(jsa.id);
                    }}
                    className="flex-1"
                  >
                    {t('common.view')}
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleOpenEdit(jsa)}
                    className="flex-1"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    {t('common.edit')}
                  </Button>
                  
                  {jsa.status === 'draft' && (
                    <Button
                      size="sm"
                      onClick={() => handleUpdateStatus(jsa.id, 'active')}
                      className="flex-1"
                    >
                      {t('common.active')}
                    </Button>
                  )}
                  
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDeleteJSA(jsa.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create JSA Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        console.log('Dialog state change:', open);
        try {
          setShowCreateDialog(open);
        } catch (err) {
          console.error('Error in dialog state change:', err);
        }
      }}>
        <DialogContent className="w-[calc(100%-1rem)] sm:w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>{t('jsa.createJsaTitle')}</DialogTitle>
            <DialogDescription>
              Builds a draft from your project JHA, approved library, safety manual and SOPs; AI only fills the gaps.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Site Photo Analysis - First Step */}
            <Card className="border-2 border-dashed border-amber-300 bg-amber-50/50">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 font-semibold text-amber-800 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  Take a Photo of Your Work Area
                </div>
                <p className="text-xs text-amber-700">
                  Snap a picture of your job site and AI will automatically identify hazards and suggest mitigations.
                </p>
                
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoCapture}
                  className="hidden"
                  data-testid="jsa-photo-input"
                />
                
                {!sitePhotoPreview ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-amber-400 text-amber-800 hover:bg-amber-100"
                    onClick={() => photoInputRef.current?.click()}
                    data-testid="jsa-take-photo-btn"
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Take Photo / Upload Image
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div className="relative">
                      <img
                        src={sitePhotoPreview}
                        alt="Job site"
                        className="w-full max-h-48 object-cover rounded-lg border"
                        data-testid="jsa-photo-preview"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={removePhoto}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                      {uploadingPhoto && (
                        <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                          Saving photo...
                        </div>
                      )}
                    </div>
                    
                    {analyzingPhoto && (
                      <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-100 p-3 rounded-lg">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        AI is analyzing your photo for hazards...
                      </div>
                    )}
                    
                    {photoAnalysisResult && (
                      <Card className="bg-green-50 border-green-200">
                        <CardContent className="p-3 space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-sm text-green-800">
                              <CheckCircle className="inline w-4 h-4 mr-1" />
                              AI Analysis Complete
                            </h4>
                          </div>
                          {photoAnalysisResult.summary && (
                            <p className="text-xs text-green-700">{photoAnalysisResult.summary}</p>
                          )}
                          
                          {/* Detected Tools */}
                          {photoAnalysisResult.toolsDetected && (
                            <div className="bg-white rounded p-2 border border-blue-100">
                              <span className="text-xs font-medium text-blue-700">Tools/Equipment Detected: </span>
                              <span className="text-xs text-gray-700">{photoAnalysisResult.toolsDetected}</span>
                              {formData.toolsBeingUsed && (
                                <span className="text-xs text-green-600 ml-1">(auto-filled below — you can edit it)</span>
                              )}
                            </div>
                          )}
                          
                          {/* Suggested Task */}
                          {photoAnalysisResult.suggestedTask && (
                            <div className="bg-white rounded p-2 border border-purple-100">
                              <span className="text-xs font-medium text-purple-700">Suggested Task: </span>
                              <span className="text-xs text-gray-700">{photoAnalysisResult.suggestedTask}</span>
                              {formData.taskDescription && (
                                <span className="text-xs text-green-600 ml-1">(auto-filled below — you can edit it)</span>
                              )}
                            </div>
                          )}
                          
                          {/* Hazards */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-red-700">
                              {photoAnalysisResult.hazards?.length || 0} Hazards Found
                            </span>
                            <Button
                              type="button"
                              size="sm"
                              onClick={addPhotoHazardsToForm}
                              data-testid="jsa-add-photo-hazards-btn"
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <PlusCircle className="mr-1 h-3 w-3" />
                              Add All Hazards to JSA
                            </Button>
                          </div>
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {photoAnalysisResult.hazards?.map((h, i) => (
                              <div key={i} className="text-xs bg-white rounded p-2 border border-green-100">
                                <span className="font-medium text-red-700">{h.hazard}</span>
                                <span className="text-gray-500"> — {h.mitigation}</span>
                                {h.ppe && <span className="text-blue-600"> (PPE: {h.ppe})</span>}
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 italic">
                            All fields are editable after adding to the form.
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Task Description */}
            <div className="space-y-2">
              <Label>{t('jsa.taskWorkerDoing')}</Label>
              <Textarea
                value={formData.taskDescription}
                onChange={(e) => setFormData({ ...formData, taskDescription: e.target.value })}
                placeholder="e.g., Excavation and trenching work, Scaffold erection, Welding operations..."
                rows={2}
              />
            </div>

            {/* Tools Being Used */}
            <div className="space-y-2">
              <Label>{t('jsa.toolsChemicals')}</Label>
              <Input
                value={formData.toolsBeingUsed}
                onChange={(e) => handleToolsChange(e.target.value)}
                onBlur={(e) => handleToolsChange(e.target.value)}
                placeholder="e.g., acetone, welding equipment, circular saw, propane..."
              />
              <div className="flex gap-2 items-center">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleFetchSdsPpe}
                  disabled={fetchingSdsPpe || !formData.toolsBeingUsed.trim()}
                  className="flex-shrink-0"
                >
                  {fetchingSdsPpe ? (
                    <>
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      Searching SDS...
                    </>
                  ) : (
                    <>
                      <FileText className="mr-2 h-3 w-3" />
                      Get PPE from SDS Library
                    </>
                  )}
                </Button>
                <p className="text-xs text-gray-500">
                  💡 Fetch PPE requirements from Safety Data Sheets
                </p>
              </div>

              {/* SDS PPE Recommendations Display */}
              {(sdsPpeRecommendations.length > 0 || matchedSdsDocuments.length > 0) && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 space-y-3">
                    <h4 className="font-semibold text-sm text-blue-900 mb-3">
                      🧪 SDS Library Match Results
                    </h4>
                    
                    {/* Matched SDS Documents with Hazards and Selection */}
                    {matchedSdsDocuments.length > 0 && (
                      <div className="space-y-3">
                        {/* Select All / Deselect All controls */}
                        <div className="flex items-center justify-between bg-white rounded-lg p-2 border border-blue-200">
                          <span className="text-xs font-medium text-gray-700">
                            {selectedSdsIds.size} of {matchedSdsDocuments.length} selected
                          </span>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => toggleAllSds(true)}
                              className="text-xs h-7 px-2"
                            >
                              Select All
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => toggleAllSds(false)}
                              className="text-xs h-7 px-2"
                            >
                              Deselect All
                            </Button>
                          </div>
                        </div>
                        
                        {matchedSdsDocuments.map((sds, idx) => (
                          <div 
                            key={idx} 
                            className={`bg-white rounded-lg p-3 border-2 cursor-pointer transition-all ${
                              selectedSdsIds.has(sds.sds_id) 
                                ? 'border-green-500 bg-green-50' 
                                : 'border-gray-200 hover:border-blue-300'
                            }`}
                            onClick={() => toggleSdsSelection(sds.sds_id)}
                          >
                            <div className="flex items-start gap-3">
                              {/* Checkbox */}
                              <div className="pt-0.5">
                                <input
                                  type="checkbox"
                                  checked={selectedSdsIds.has(sds.sds_id)}
                                  onChange={() => toggleSdsSelection(sds.sds_id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                                />
                              </div>
                              
                              {/* SDS Details */}
                              <div className="flex-1">
                                <p className="font-semibold text-sm text-gray-900 mb-1">
                                  📋 {sds.product_name || sds.chemical_name}
                                  {sds.concentration && (
                                    <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded font-medium">
                                      {sds.concentration}
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-gray-500 mb-2">
                                  {sds.manufacturer && `Manufacturer: ${sds.manufacturer}`}
                                  {sds.chemical_name && sds.chemical_name !== sds.product_name && ` • Chemical: ${sds.chemical_name}`}
                                </p>
                                
                                {/* Physical Hazards */}
                                {sds.physical_hazards && Array.isArray(sds.physical_hazards) && sds.physical_hazards.length > 0 && (
                                  <div className="mb-2">
                                    <p className="text-xs font-medium text-orange-700 mb-1">⚠️ Physical Hazards:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {sds.physical_hazards.map((hazard, i) => (
                                        <span key={i} className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded border border-orange-300">
                                          {hazard}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Health Hazards */}
                                {sds.health_hazards && Array.isArray(sds.health_hazards) && sds.health_hazards.length > 0 && (
                                  <div className="mb-2">
                                    <p className="text-xs font-medium text-red-700 mb-1">🏥 Health Hazards:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {sds.health_hazards.map((hazard, i) => (
                                        <span key={i} className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded border border-red-300">
                                          {hazard}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* PPE Required */}
                                {sds.ppe_required && Array.isArray(sds.ppe_required) && sds.ppe_required.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-blue-700 mb-1">🦺 PPE Required:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {sds.ppe_required.map((ppe, i) => (
                                        <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded border border-blue-300">
                                          {ppe}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* Attach Selected Button */}
                        <Button
                          type="button"
                          size="sm"
                          onClick={applySelectedSdsToJsa}
                          disabled={selectedSdsIds.size === 0}
                          className="w-full bg-green-600 hover:bg-green-700"
                        >
                          ✓ Attach {selectedSdsIds.size} Selected SDS to JSA
                        </Button>
                        
                        {formData.linkedSdsDocuments?.length > 0 && (
                          <p className="text-xs text-green-600 font-semibold text-center">
                            ✓ {formData.linkedSdsDocuments.length} SDS document(s) attached to this JSA
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Combined PPE Summary */}
                    {sdsPpeRecommendations.length > 0 && (
                      <div className="pt-2 border-t border-blue-200">
                        <p className="text-xs font-medium text-blue-900 mb-2">📦 Combined PPE Requirements:</p>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {sdsPpeRecommendations.map((ppe, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full border border-blue-300"
                            >
                              {ppe}
                            </span>
                          ))}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleApplySdsPpe}
                          className="w-full"
                        >
                          Apply These PPE Requirements to Hazards
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Today's work context — drives the JHA/library/manual/SOP search and flags changed conditions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Materials / chemicals today</Label>
                <Input
                  value={formData.materials}
                  onChange={(e) => setFormData({ ...formData, materials: e.target.value })}
                  placeholder="e.g., modified bitumen membrane, propane, PVC primer"
                  data-testid="jsa-materials-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Site conditions today</Label>
                <Input
                  value={formData.conditions}
                  onChange={(e) => setFormData({ ...formData, conditions: e.target.value })}
                  placeholder="e.g., wet deck, 18 mph wind, occupied building below"
                  data-testid="jsa-conditions-input"
                />
              </div>
            </div>

            {/* Project JHA source — auto-matched, supervisor can override */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Project JHA to pull from</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={fetchJhaCandidates}
                  disabled={!formData.taskDescription.trim()}
                  data-testid="jsa-find-jha-btn"
                >
                  <RefreshCw className="mr-2 h-3 w-3" />
                  Find matching JHAs
                </Button>
              </div>
              {jhaCandidates.length > 0 ? (
                <Select
                  value={formData.sourceJhaId || 'auto'}
                  onValueChange={(v) => setFormData({ ...formData, sourceJhaId: v === 'auto' ? '' : v })}
                >
                  <SelectTrigger data-testid="jsa-jha-select">
                    <SelectValue placeholder="Auto-match the best completed JHA" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto-match (best completed JHA)</SelectItem>
                    {jhaCandidates.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.projectName} — rev {c.revision} · {c.matchedStepCount}/{c.totalSteps} steps match
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs text-slate-500">
                  Completed and approved project JHAs are searched automatically alongside your
                  learning library, safety manual, SOPs and SDS.
                </p>
              )}
            </div>

            {/* Auto-Fill Button */}
            <Button
              onClick={handleAutoPopulate}
              disabled={autoPopulating || !formData.taskDescription.trim()}
              className="w-full"
              size="lg"
              data-testid="jsa-auto-populate-btn"
            >
              {autoPopulating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {draftProgress || 'Building your draft…'}
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-5 w-5" />
                  Build Draft from Our Records + AI
                </>
              )}
            </Button>
            <p className="text-xs text-gray-500 text-center">
              Searches the project JHA, approved library, safety manual and SOPs first; AI only
              develops what is missing. You can leave this screen — the draft keeps generating.
            </p>

            {draftError && (
              <div className="rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-800"
                   data-testid="jsa-draft-error">
                {draftError}
                <Button type="button" size="sm" variant="outline" className="ml-2 h-6"
                        onClick={handleAutoPopulate} data-testid="jsa-draft-retry-btn">
                  Retry
                </Button>
              </div>
            )}

            {draftStages && (
              <Card className="bg-slate-50 border-slate-200" data-testid="jsa-draft-source-panel">
                <CardContent className="p-3 space-y-2 text-xs text-slate-700">
                  <p className="font-semibold text-slate-900">What this draft was built from</p>
                  {draftSources?.jha && (
                    <p data-testid="jsa-draft-jha-source">
                      Project JHA: <span className="font-medium">{draftSources.jha.title}</span> — rev{' '}
                      {draftSources.jha.revision} ({draftSources.jha.status},{' '}
                      {draftSources.jha.selectedBy === 'supervisor' ? 'you picked it' : 'auto-matched'})
                    </p>
                  )}
                  <p>
                    Retrieved: {draftStages.retrieval?.libraryEntries || 0} library entr(ies) ·{' '}
                    {draftStages.retrieval?.manualDocs || 0} manual/SOP doc(s) ·{' '}
                    {draftStages.retrieval?.lotoEquipment || 0} LOTO ·{' '}
                    {draftStages.retrieval?.sdsDocs || 0} SDS ·{' '}
                    {draftStages.retrieval?.jhaHazards || 0} JHA hazard row(s)
                  </p>
                  <p>
                    Organized by{' '}
                    {draftStages.organize?.skipped
                      ? 'no AI — your approved library already covered this task'
                      : `${draftStages.organize?.model || '—'} · ${draftStages.organize?.rowsSourceSupported || 0} source-supported row(s)`}
                  </p>
                  <p>
                    Gaps developed by{' '}
                    {draftStages.gapFill?.skipped
                      ? 'no AI — nothing was missing'
                      : `${draftStages.gapFill?.model || '—'} · ${draftStages.gapFill?.newRows || 0} new row(s), ${draftStages.gapFill?.augmentedRows || 0} row(s) extended`}
                  </p>
                  {(draftStages.organize?.scopeDifferences || []).length > 0 && (
                    <div>
                      <p className="font-medium text-amber-800">Differences from the JHA scope / today's work:</p>
                      <ul className="list-disc pl-4">
                        {draftStages.organize.scopeDifferences.map((d, i) => <li key={i}>{d}</li>)}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Location and Date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('jsa.location')}</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Job site location"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('common.date')} *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
            </div>

            {/* Emergency Equipment Location */}
            <div className="space-y-2">
              <Label>{t('jsa.emergencyEquipmentLoc')}</Label>
              <Textarea
                value={formData.emergencyEquipmentLocation}
                onChange={(e) => setFormData({ ...formData, emergencyEquipmentLocation: e.target.value })}
                placeholder="e.g., Safety shower - 50ft south near electrical panel, Eye wash station - North wall by entrance, First aid kit - Supervisor trailer, Fire extinguisher - Near welding area"
                rows={3}
              />
              <p className="text-xs text-slate-500">
                Document locations of safety showers, eye wash stations, first aid kits, fire extinguishers, and other emergency equipment
              </p>
            </div>

            {/* Hazards Section */}
            <div className="space-y-3">
              <Label className="text-lg font-semibold">{t('jsa.stepsAndHazards')}</Label>
              
              {/* Add New Hazard - Auto-populating */}
              <Card className="bg-gray-50">
                <CardContent className="p-4 space-y-3">
                  <div className="space-y-2">
                    <Label>{t('jsa.hazardDescription')} *</Label>
                    <Textarea
                      value={newHazard.hazard}
                      onChange={(e) => handleHazardChange(e.target.value)}
                      placeholder="Start typing the hazard... Mitigation will auto-populate"
                      rows={2}
                    />
                    {generatingMitigation && (
                      <div className="flex items-center text-xs text-blue-600">
                        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                        Auto-generating mitigation...
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Mitigation Strategy * {newHazard.mitigation && '✓'}</Label>
                    <Textarea
                      value={newHazard.mitigation}
                      onChange={(e) => setNewHazard({ ...newHazard, mitigation: e.target.value })}
                      placeholder="Mitigation will appear automatically as you type the hazard above..."
                      rows={2}
                    />
                    <p className="text-xs text-gray-500">
                      💡 Mitigation auto-populates 1.5 seconds after you stop typing the hazard
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('jsa.requiredPPE')}</Label>
                    <Input
                      value={newHazard.ppe}
                      onChange={(e) => setNewHazard({ ...newHazard, ppe: e.target.value })}
                      placeholder="e.g., Safety glasses, Hard hat, Gloves, Steel-toe boots"
                    />
                    <p className="text-xs text-gray-500">
                      🦺 Specify all PPE required for this hazard
                    </p>
                  </div>
                  
                  <Button onClick={handleAddHazard} className="w-full">
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Add Hazard to List
                  </Button>
                </CardContent>
              </Card>

              {/* Existing Hazards List */}
              {formData.hazards.length > 0 && (
                <div className="space-y-2">
                  {formData.hazards.map((h, index) => (
                    <Card key={rowKey(h, index, 'hazard')}>
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 space-y-2">
                            <JsaSourceBadges hazard={h} index={index} />
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="h-4 w-4 text-red-500 mt-2 flex-shrink-0" />
                              <div className="flex-1 space-y-2">
                                <Input
                                  value={h.hazard}
                                  onChange={(e) => handleEditHazard(index, 'hazard', e.target.value)}
                                  placeholder="Hazard description"
                                  className="text-sm font-medium"
                                  data-testid={`hazard-input-${index}`}
                                />
                                <Input
                                  value={h.mitigation}
                                  onChange={(e) => handleEditHazard(index, 'mitigation', e.target.value)}
                                  placeholder="Mitigation / Control measure"
                                  className="text-xs"
                                  data-testid={`mitigation-input-${index}`}
                                />
                                <Input
                                  value={h.ppe || ''}
                                  onChange={(e) => handleEditHazard(index, 'ppe', e.target.value)}
                                  placeholder="Required PPE"
                                  className="text-xs"
                                  data-testid={`ppe-input-${index}`}
                                />
                              </div>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveHazard(index)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Assign Workers */}
            <div className="space-y-2">
              <Label>{t('jsa.assignWorkersOptional')}</Label>
              
              {/* Search Input */}
              <Input
                value={workerSearchQuery}
                onChange={(e) => setWorkerSearchQuery(e.target.value)}
                placeholder="Search workers by name..."
                className="mb-2"
              />
              
              {/* Searchable Dropdown List */}
              {workerSearchQuery && (
                <div className="border rounded-md max-h-48 overflow-y-auto bg-white">
                  {workers
                    .filter(worker => 
                      worker.name.toLowerCase().includes(workerSearchQuery.toLowerCase()) &&
                      !selectedWorkers.find(sw => sw.id === worker.id)
                    )
                    .map(worker => (
                      <div
                        key={worker.id}
                        onClick={() => {
                          setSelectedWorkers([...selectedWorkers, worker]);
                          setWorkerSearchQuery('');
                        }}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                      >
                        <div>
                          <p className="font-medium">{worker.name}</p>
                          <p className="text-xs text-gray-500">{worker.email}</p>
                        </div>
                      </div>
                    ))}
                  {workers.filter(w => 
                    w.name.toLowerCase().includes(workerSearchQuery.toLowerCase()) &&
                    !selectedWorkers.find(sw => sw.id === w.id)
                  ).length === 0 && (
                    <p className="px-3 py-2 text-sm text-gray-500">No workers found</p>
                  )}
                </div>
              )}
              
              {/* Selected Workers */}
              {selectedWorkers.length > 0 && (
                <div className="space-y-2 mt-2">
                  <p className="text-sm font-medium">Selected Workers:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedWorkers.map(worker => (
                      <div
                        key={worker.id}
                        className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center gap-2"
                      >
                        <span className="text-sm">{worker.name}</span>
                        <button
                          onClick={() => setSelectedWorkers(selectedWorkers.filter(w => w.id !== worker.id))}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <p className="text-xs text-gray-500">
                Search and click to add workers to this JSA
              </p>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>{t('jsa.additionalNotes')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional safety information..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              resetForm();
            }}>
              Cancel
            </Button>
            {formData.hazards.length > 0 && (
              <Button variant="secondary" onClick={() => setShowSaveTemplateDialog(true)}>
                <FileText className="mr-2 h-4 w-4" />
                Save as Template
              </Button>
            )}
            <Button onClick={handleCreateJSA}>
              {t('jsa.createJsa')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit JSA Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="w-[calc(100%-1rem)] sm:w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>{t('jsa.editJsaTitle')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-task">{t('jsa.taskDescription')} *</Label>
              <Input
                id="edit-task"
                value={formData.taskDescription}
                onChange={(e) => setFormData({ ...formData, taskDescription: e.target.value })}
                placeholder="Brief description of the task"
              />
            </div>

            <div>
              <Label htmlFor="edit-location">{t('jsa.location')} *</Label>
              <Input
                id="edit-location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Job site location"
              />
            </div>

            <div>
              <Label htmlFor="edit-date">{t('common.date')} *</Label>
              <Input
                id="edit-date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-tools">{t('jsa.toolsChemicals')}</Label>
              <Textarea
                id="edit-tools"
                value={formData.toolsBeingUsed}
                onChange={(e) => handleToolsChange(e.target.value)}
                onBlur={(e) => handleToolsChange(e.target.value)}
                placeholder="e.g., acetone, welding equipment, circular saw, propane..."
              />
              <div className="flex gap-2 items-center">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleFetchSdsPpe}
                  disabled={fetchingSdsPpe || !formData.toolsBeingUsed.trim()}
                  className="flex-shrink-0"
                >
                  {fetchingSdsPpe ? (
                    <>
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      Searching SDS...
                    </>
                  ) : (
                    <>
                      <FileText className="mr-2 h-3 w-3" />
                      Get PPE from SDS Library
                    </>
                  )}
                </Button>
                <p className="text-xs text-gray-500">
                  💡 Fetch PPE requirements from Safety Data Sheets
                </p>
              </div>

              {/* SDS PPE Recommendations Display */}
              {(sdsPpeRecommendations.length > 0 || matchedSdsDocuments.length > 0) && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 space-y-3">
                    <h4 className="font-semibold text-sm text-blue-900 mb-3">
                      🧪 SDS Library Match Results
                    </h4>
                    
                    {/* Matched SDS Documents with Hazards and Selection */}
                    {matchedSdsDocuments.length > 0 && (
                      <div className="space-y-3">
                        {/* Select All / Deselect All controls */}
                        <div className="flex items-center justify-between bg-white rounded-lg p-2 border border-blue-200">
                          <span className="text-xs font-medium text-gray-700">
                            {selectedSdsIds.size} of {matchedSdsDocuments.length} selected
                          </span>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => toggleAllSds(true)}
                              className="text-xs h-7 px-2"
                            >
                              Select All
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => toggleAllSds(false)}
                              className="text-xs h-7 px-2"
                            >
                              Deselect All
                            </Button>
                          </div>
                        </div>
                        
                        {matchedSdsDocuments.map((sds, idx) => (
                          <div 
                            key={idx} 
                            className={`bg-white rounded-lg p-3 border-2 cursor-pointer transition-all ${
                              selectedSdsIds.has(sds.sds_id) 
                                ? 'border-green-500 bg-green-50' 
                                : 'border-gray-200 hover:border-blue-300'
                            }`}
                            onClick={() => toggleSdsSelection(sds.sds_id)}
                          >
                            <div className="flex items-start gap-3">
                              {/* Checkbox */}
                              <div className="pt-0.5">
                                <input
                                  type="checkbox"
                                  checked={selectedSdsIds.has(sds.sds_id)}
                                  onChange={() => toggleSdsSelection(sds.sds_id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                                />
                              </div>
                              
                              {/* SDS Details */}
                              <div className="flex-1">
                                <p className="font-semibold text-sm text-gray-900 mb-1">
                                  📋 {sds.product_name || sds.chemical_name}
                                  {sds.concentration && (
                                    <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded font-medium">
                                      {sds.concentration}
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-gray-500 mb-2">
                                  {sds.manufacturer && `Manufacturer: ${sds.manufacturer}`}
                                  {sds.chemical_name && sds.chemical_name !== sds.product_name && ` • Chemical: ${sds.chemical_name}`}
                                </p>
                                
                                {/* Physical Hazards */}
                                {sds.physical_hazards && Array.isArray(sds.physical_hazards) && sds.physical_hazards.length > 0 && (
                                  <div className="mb-2">
                                    <p className="text-xs font-medium text-orange-700 mb-1">⚠️ Physical Hazards:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {sds.physical_hazards.map((hazard, i) => (
                                        <span key={i} className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded border border-orange-300">
                                          {hazard}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Health Hazards */}
                                {sds.health_hazards && Array.isArray(sds.health_hazards) && sds.health_hazards.length > 0 && (
                                  <div className="mb-2">
                                    <p className="text-xs font-medium text-red-700 mb-1">🏥 Health Hazards:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {sds.health_hazards.map((hazard, i) => (
                                        <span key={i} className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded border border-red-300">
                                          {hazard}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* PPE Required */}
                                {sds.ppe_required && Array.isArray(sds.ppe_required) && sds.ppe_required.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-blue-700 mb-1">🦺 PPE Required:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {sds.ppe_required.map((ppe, i) => (
                                        <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded border border-blue-300">
                                          {ppe}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* Attach Selected Button */}
                        <Button
                          type="button"
                          size="sm"
                          onClick={applySelectedSdsToJsa}
                          disabled={selectedSdsIds.size === 0}
                          className="w-full bg-green-600 hover:bg-green-700"
                        >
                          ✓ Attach {selectedSdsIds.size} Selected SDS to JSA
                        </Button>
                        
                        {formData.linkedSdsDocuments?.length > 0 && (
                          <p className="text-xs text-green-600 font-semibold text-center">
                            ✓ {formData.linkedSdsDocuments.length} SDS document(s) attached to this JSA
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Combined PPE Summary */}
                    {sdsPpeRecommendations.length > 0 && (
                      <div className="pt-2 border-t border-blue-200">
                        <p className="text-xs font-medium text-blue-900 mb-2">📦 Combined PPE Requirements:</p>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {sdsPpeRecommendations.map((ppe, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full border border-blue-300"
                            >
                              {ppe}
                            </span>
                          ))}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleApplySdsPpe}
                          className="w-full"
                        >
                          Apply These PPE Requirements to Hazards
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            <div>
              <Label>{t('jsa.stepsAndHazards')} *</Label>
              <div className="space-y-2 mb-2">
                {formData.hazards.map((h, idx) => (
                  <div key={rowKey(h, idx, 'hazard')} className="border rounded p-3 bg-gray-50">
                    <div className="flex items-start justify-between mb-2">
                      <p className="font-semibold text-sm">Hazard {idx + 1}</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveHazard(idx)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs font-medium text-gray-600">Hazard:</label>
                        <Input
                          value={h.hazard}
                          onChange={(e) => {
                            const updated = [...formData.hazards];
                            updated[idx] = { ...updated[idx], hazard: e.target.value };
                            setFormData({ ...formData, hazards: updated });
                          }}
                          className="text-sm mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600">Mitigation:</label>
                        <Input
                          value={h.mitigation}
                          onChange={(e) => {
                            const updated = [...formData.hazards];
                            updated[idx] = { ...updated[idx], mitigation: e.target.value };
                            setFormData({ ...formData, hazards: updated });
                          }}
                          className="text-sm mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-blue-600">🦺 PPE (Optional - Adjust as needed):</label>
                        <Input
                          value={h.ppe || ''}
                          onChange={(e) => {
                            const updated = [...formData.hazards];
                            updated[idx] = { ...updated[idx], ppe: e.target.value };
                            setFormData({ ...formData, hazards: updated });
                          }}
                          placeholder="Add or adjust PPE requirements"
                          className="text-sm mt-1 border-blue-200"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border rounded p-3 space-y-2">
                <Input
                  value={newHazard.hazard}
                  onChange={(e) => handleHazardChange(e.target.value)}
                  placeholder="Describe a potential hazard"
                />
                <div className="relative">
                  <Textarea
                    value={newHazard.mitigation}
                    onChange={(e) => setNewHazard({ ...newHazard, mitigation: e.target.value })}
                    placeholder="How will this hazard be mitigated?"
                  />
                  {generatingMitigation && (
                    <div className="absolute top-2 right-2 text-blue-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                  )}
                </div>
                <Input
                  value={newHazard.ppe}
                  onChange={(e) => setNewHazard({ ...newHazard, ppe: e.target.value })}
                  placeholder="PPE (Optional - e.g., Safety glasses, Hard hat) - Can exceed OSHA standards"
                />
                <Button size="sm" onClick={handleAddHazard} variant="outline">
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Add Hazard
                </Button>
              </div>
            </div>

            <div>
              <Label>{t('jsa.assignWorkersOptional')}</Label>
              <div className="space-y-2">
                <div className="relative">
                  <Input
                    placeholder="Search workers by name..."
                    value={workerSearch}
                    onChange={(e) => setWorkerSearch(e.target.value)}
                    onFocus={() => setShowWorkerDropdown(true)}
                    onBlur={() => setTimeout(() => setShowWorkerDropdown(false), 200)}
                  />
                  {showWorkerDropdown && filteredWorkers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredWorkers.map(worker => (
                        <div
                          key={worker.id}
                          className="p-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            if (!selectedWorkers.find(w => w.id === worker.id)) {
                              setSelectedWorkers([...selectedWorkers, worker]);
                            }
                            setWorkerSearch('');
                            setShowWorkerDropdown(false);
                          }}
                        >
                          <p className="font-medium text-sm">{worker.name}</p>
                          <p className="text-xs text-gray-500">{worker.email}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {selectedWorkers.map(worker => (
                    <div key={worker.id} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center">
                      {worker.name}
                      <button
                        onClick={() => setSelectedWorkers(selectedWorkers.filter(w => w.id !== worker.id))}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="edit-notes">{t('jsa.additionalNotes')}</Label>
              <Textarea
                id="edit-notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional safety information"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditDialog(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button onClick={handleUpdateJSA}>
              Update JSA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View JSA Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="w-[calc(100%-1rem)] sm:w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Job Safety Analysis</DialogTitle>
          </DialogHeader>

          {selectedJsa && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Task</h3>
                <p>{selectedJsa.taskDescription}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-1">Location</h3>
                  <p className="text-gray-600">{selectedJsa.location || 'Not specified'}</p>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Date</h3>
                  <p className="text-gray-600">{new Date(selectedJsa.date).toLocaleDateString()}</p>
                </div>
              </div>

              {selectedJsa.emergencyEquipmentLocation && (
                <div>
                  <h3 className="font-semibold mb-2">Emergency Equipment Location</h3>
                  <p className="text-gray-600 whitespace-pre-line">{selectedJsa.emergencyEquipmentLocation}</p>
                </div>
              )}

              {/* Site Photos */}
              {selectedJsa.photos && selectedJsa.photos.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Site Photos</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {selectedJsa.photos.map((photoId, idx) => {
                      const token = localStorage.getItem('token');
                      const cleanId = photoId.replace('gridfs://', '');
                      return (
                        <img
                          key={idx}
                          src={`${API}/jsa/photo/${cleanId}?token=${token}`}
                          alt={`Site photo ${idx + 1}`}
                          className="w-full h-32 object-cover rounded-lg border cursor-pointer hover:opacity-80"
                          data-testid={`jsa-photo-${idx}`}
                          onClick={() => window.open(`${API}/jsa/photo/${cleanId}?token=${token}`, '_blank')}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <h3 className="font-semibold mb-2">Hazards & Mitigations</h3>
                <div className="space-y-2">
                  {selectedJsa.hazards.map((h, index) => (
                    <Card key={index} className={h.source === 'company_loto_sop' ? 'border-l-4 border-l-orange-500' : ''}>
                      <CardContent className="p-3">
                        <div className="flex items-start gap-2">
                          {h.source === 'company_loto_sop' ? (
                            <span className="text-orange-600 mt-1 flex-shrink-0" title="From your LOTO SOP">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
                            </span>
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-sm">{h.hazard}</p>
                              {h.source === 'company_loto_sop' && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full" data-testid="loto-sop-badge">
                                  From your LOTO SOP
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 mt-1">
                              <span className="font-semibold">Mitigation:</span> {h.mitigation}
                            </p>
                            {h.ppe && (
                              <p className="text-xs text-blue-600 mt-1">
                                <span className="font-semibold">🦺 PPE:</span> {h.ppe}
                              </p>
                            )}
                            {h.source === 'company_loto_sop' && h.sourceDocumentTitle && (
                              <p className="text-[11px] text-orange-700 mt-1 italic">
                                Source: {h.sourceDocumentTitle}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {selectedJsa.notes && (
                <div>
                  <h3 className="font-semibold mb-2">{t('jsa.additionalNotes')}</h3>
                  <p className="text-gray-600">{selectedJsa.notes}</p>
                </div>
              )}

              {/* Attached SDS Documents */}
              {selectedJsa.linkedSdsDocuments && selectedJsa.linkedSdsDocuments.length > 0 && (
                <Card className="border-orange-200 bg-orange-50">
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4 text-orange-600" />
                      📋 Attached Safety Data Sheets ({selectedJsa.linkedSdsDocuments.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {selectedJsa.linkedSdsDocuments.map((sds, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white border border-orange-200 rounded p-2">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">{sds.chemical_name}</p>
                          {sds.manufacturer && (
                            <p className="text-xs text-gray-600">{sds.manufacturer}</p>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openAuthed(`/sds/download/${sds.sds_id}`)}
                          className="bg-orange-600 text-white hover:bg-orange-700"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          View SDS
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {(selectedJsa.assignedWorkerNames || (selectedJsa.assignedWorkers && selectedJsa.assignedWorkers.length > 0)) && (
                <div>
                  <h3 className="font-semibold mb-2">Assigned Workers</h3>
                  <p className="text-sm text-gray-600">
                    {selectedJsa.assignedWorkerNames || selectedJsa.assignedWorkers.join(', ')}
                  </p>
                </div>
              )}

              {/* Linked Documents */}
              {(selectedJsa.linkedAudits?.length > 0 || selectedJsa.linkedIncidents?.length > 0) && (
                <Card className="border-blue-200 bg-blue-50">
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      🔗 Related Safety Documents at This Location
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {selectedJsa.linkedAudits?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-blue-900 mb-2">Safety Audits ({selectedJsa.linkedAudits.length})</p>
                        <div className="space-y-2">
                          {selectedJsa.linkedAudits.map((audit) => (
                            <div key={audit.id} className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 justify-start bg-white hover:bg-blue-100"
                              >
                                <Eye className="w-3 h-3 mr-2 text-blue-600" />
                                Audit on {audit.auditDate}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveLinkedAudit(selectedJsa.id, audit.id)}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                title="Remove this linked audit"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedJsa.linkedIncidents?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-red-900 mb-2">Incident Reports ({selectedJsa.linkedIncidents.length})</p>
                        <div className="space-y-2">
                          {selectedJsa.linkedIncidents.map((incident) => (
                            <Button
                              key={incident.id}
                              variant="outline"
                              size="sm"
                              className="w-full justify-start bg-white hover:bg-red-100 border-red-200"
                            >
                              <AlertTriangle className="w-3 h-3 mr-2 text-red-600" />
                              {incident.incidentType} - {incident.severity}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Worker Signatures Section with Training Status */}
              {selectedJsa.workerAcknowledgments && Object.keys(selectedJsa.workerAcknowledgments).length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h3 className="font-semibold mb-3 flex items-center justify-between">
                    <span className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Worker Signatures
                    </span>
                    {trainingStatus?.hasRequiredCourses && (
                      <span className="text-xs font-normal text-gray-500">Training Status</span>
                    )}
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(selectedJsa.workerAcknowledgments).map(([workerId, ack]) => {
                      const workerTraining = trainingStatus?.workers?.[workerId];
                      const hasMissingTraining = workerTraining && !workerTraining.verified;
                      
                      return (
                        <div key={workerId} className={`${hasMissingTraining ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'} border rounded-lg p-3`}>
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className={`text-sm font-semibold ${hasMissingTraining ? 'text-orange-900' : 'text-green-900'}`}>
                                  {ack.printedName || ack.signature || 'Worker'}
                                </p>
                                {ack.signedOnDevice && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    <PenLine className="w-3 h-3 mr-1" />
                                    Signed on device
                                  </span>
                                )}
                                {hasMissingTraining && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                    ⚠️ Training Incomplete
                                  </span>
                                )}
                                {workerTraining?.verified && trainingStatus?.hasRequiredCourses && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                    ✓ Training Verified
                                  </span>
                                )}
                              </div>
                              <p className={`text-xs ${hasMissingTraining ? 'text-orange-700' : 'text-green-700'}`}>
                                Signed: {new Date(ack.timestamp).toLocaleString()}
                              </p>
                              {ack.signedOnDevice && ack.witnessedBy?.name && (
                                <p className="text-xs text-gray-500">Witnessed by: {ack.witnessedBy.name}</p>
                              )}
                              {/* Show missing/expired training details */}
                              {hasMissingTraining && (
                                <div className="mt-2 text-xs text-orange-800">
                                  {workerTraining.missing?.length > 0 && (
                                    <p>⚠️ Missing: {workerTraining.missing.join(', ')}</p>
                                  )}
                                  {workerTraining.expired?.length > 0 && (
                                    <p>⏰ Expired: {workerTraining.expired.join(', ')}</p>
                                  )}
                                </div>
                              )}
                            </div>
                            {ack.signatureImage ? (
                              <img src={ack.signatureImage} alt="signature" className="h-12 bg-white border border-gray-200 rounded" />
                            ) : (
                              <svg className={`w-6 h-6 ${hasMissingTraining ? 'text-orange-500' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Unsigned Workers Warning */}
              {/* On-Device Co-Sign Section — for workers who don't have their phone */}
              {selectedJsa.status !== 'submitted' && (
                (() => {
                  const acks = selectedJsa.workerAcknowledgments || {};
                  const unsigned = (selectedJsa.assignedWorkers || []).filter(
                    id => !acks[id]?.acknowledged
                  );
                  const canCoSign = user && (
                    user.id === selectedJsa.supervisorId ||
                    ['company_admin', 'division_admin', 'super_admin'].includes(user.role)
                  );
                  if (!canCoSign) return null;

                  return (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold flex items-center">
                          <PenLine className="w-5 h-5 mr-2 text-blue-600" />
                          On-Device Sign-Off
                        </h3>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={openCoSignForManual}
                          data-testid="jsa-add-manual-signer-btn"
                        >
                          <UserPlus className="w-4 h-4 mr-1" />
                          Add crew member without account
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        For workers without their phone or unregistered crew (day labor, subs), capture signatures here.
                      </p>
                      {unsigned.length > 0 && (
                        <div className="space-y-2">
                          {unsigned.map(workerId => {
                            const w = workers.find(x => x.id === workerId);
                            const workerName = w?.name || (selectedJsa.assignedWorkerNames?.split(', ') || [])[selectedJsa.assignedWorkers.indexOf(workerId)] || 'Unknown Worker';
                            return (
                              <div key={workerId} className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-lg p-3">
                                <div>
                                  <p className="text-sm font-medium text-orange-900">{workerName}</p>
                                  <p className="text-xs text-orange-700">Not yet signed</p>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => openCoSignForWorker(workerId, workerName)}
                                  className="bg-blue-600 hover:bg-blue-700"
                                  data-testid={`jsa-co-sign-btn-${workerId}`}
                                >
                                  <PenLine className="w-4 h-4 mr-1" />
                                  Sign on this device
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()
              )}

              {/* Manual (Non-Account) Signatures */}
              {selectedJsa.manualSignatures && selectedJsa.manualSignatures.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <UserPlus className="w-5 h-5 mr-2 text-indigo-600" />
                    Additional Crew Signatures (No Account)
                  </h3>
                  <div className="space-y-2">
                    {selectedJsa.manualSignatures.map((sig) => (
                      <div key={sig.id} className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-indigo-900">{sig.printedName || sig.workerName}</p>
                            <p className="text-xs text-indigo-700">
                              Signed on device • {new Date(sig.timestamp).toLocaleString()}
                            </p>
                            {sig.witnessedBy?.name && (
                              <p className="text-xs text-gray-500 mt-1">Witnessed by: {sig.witnessedBy.name}</p>
                            )}
                          </div>
                          {sig.signatureImage && (
                            <img
                              src={sig.signatureImage}
                              alt="signature"
                              className="h-12 bg-white border border-gray-200 rounded"
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Audits Section (JSA-Audit Linkage Visualization) */}
              <div className="mt-6 pt-6 border-t">
                <h3 className="font-semibold mb-3 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-purple-600" />
                  Related Safety Audits
                </h3>
                {loadingRelatedAudits ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : relatedAudits.length === 0 ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-600">
                      No safety audits have referenced this JSA yet.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {relatedAudits.map((audit) => (
                      <Card key={audit.id} className="border-purple-200 hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-gray-900">{audit.location || 'Safety Audit'}</h4>
                                {audit.compliance_status && (
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    audit.compliance_status === 'COMPLIANT' ? 'bg-green-100 text-green-700' :
                                    audit.compliance_status === 'PARTIAL' ? 'bg-orange-100 text-orange-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {audit.compliance_status}
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-2">
                                <p>
                                  <span className="font-medium">Date:</span>{' '}
                                  {audit.auditDate ? new Date(audit.auditDate).toLocaleDateString() : 'N/A'}
                                </p>
                                <p>
                                  <span className="font-medium">Supervisor:</span> {audit.supervisorName || 'N/A'}
                                </p>
                              </div>
                              {audit.compliance_score !== undefined && (
                                <div className="flex items-center gap-2 text-sm mb-2">
                                  <span className="text-gray-600">Compliance Score:</span>
                                  <div className="flex-1 max-w-[150px]">
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                      <div 
                                        className={`h-1.5 rounded-full ${
                                          audit.compliance_score >= 80 ? 'bg-green-500' :
                                          audit.compliance_score >= 60 ? 'bg-orange-500' :
                                          'bg-red-500'
                                        }`}
                                        style={{ width: `${audit.compliance_score}%` }}
                                      />
                                    </div>
                                  </div>
                                  <span className="font-semibold text-xs">{audit.compliance_score}/100</span>
                                </div>
                              )}
                              {audit.match_confidence && (
                                <p className="text-xs text-gray-500">
                                  Match Confidence: {audit.match_confidence}%
                                </p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                // Open supervisor dashboard in new tab where audits are displayed
                                window.open(`/supervisor`, '_blank');
                              }}
                              className="ml-4"
                            >
                              <FileText className="w-3 h-3 mr-1" />
                              View
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    <p className="text-xs text-gray-500 mt-2">
                      {relatedAudits.length} audit{relatedAudits.length !== 1 ? 's' : ''} referenced this JSA for compliance checking
                    </p>
                  </div>
                )}
              </div>

              {/* Change History Section */}
              <div className="mt-6 pt-6 border-t">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-blue-600" />
                    Change History
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowHistoryTab(!showHistoryTab)}
                  >
                    {showHistoryTab ? 'Hide' : 'Show'}
                  </Button>
                </div>
                
                {showHistoryTab && (
                  loadingHistory ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  ) : changeHistory.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600">
                        No changes recorded yet.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                      {changeHistory.map((change) => (
                        <Card key={change.id} className="border-blue-200">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-sm font-semibold text-gray-900">
                                    {change.changeType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  </span>
                                  {change.sourceAuditId && (
                                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                      From Audit
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 mb-2">
                                  By {change.changedByName}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {new Date(change.timestamp).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              {selectedJsa && selectedJsa.status === 'draft' && (
                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                  Draft
                </span>
              )}
              {selectedJsa && selectedJsa.status === 'pending_signatures' && (
                <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                  Pending Signatures
                </span>
              )}
              {selectedJsa && selectedJsa.status === 'active' && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  Active
                </span>
              )}
              {selectedJsa && selectedJsa.status === 'submitted' && (
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Submitted
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={handlePrintJSA}
                variant="outline"
                className="gap-2"
              >
                <Printer className="w-4 h-4" />
                Print for Signatures
              </Button>
              {selectedJsa && selectedJsa.status !== 'submitted' && (
                <Button 
                  onClick={() => handleSubmitToAdmin(selectedJsa.id)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Submit to Admin
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>{t('common.close')}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* On-Device Co-Sign Dialog */}
      <Dialog open={showCoSignDialog} onOpenChange={setShowCoSignDialog}>
        <DialogContent className="max-w-lg" data-testid="jsa-cosign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5 text-blue-600" />
              {coSignTarget?.type === 'manual' ? 'Add Crew Signature' : 'Sign on This Device'}
            </DialogTitle>
            <p className="text-sm text-gray-600">
              {coSignTarget?.type === 'manual'
                ? "Capture signature for a crew member without a SafetyMind account."
                : `Capture ${coSignTarget?.workerName || 'the worker'}'s signature.`}
            </p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Printed Name *</Label>
              <Input
                value={coSignPrintedName}
                onChange={(e) => setCoSignPrintedName(e.target.value)}
                placeholder="Type full name"
                data-testid="jsa-cosign-printed-name"
              />
            </div>
            <div>
              <Label>Signature *</Label>
              <SignaturePad value={coSignSignature} onChange={setCoSignSignature} height={180} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCoSignDialog(false)} disabled={savingCoSign}>Cancel</Button>
            <Button onClick={confirmCoSign} disabled={savingCoSign} className="bg-blue-600 hover:bg-blue-700" data-testid="jsa-cosign-submit">
              {savingCoSign ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>) : 'Save Signature'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit-to-Admin Dialog — requires submitter's printed name + signature */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="max-w-lg" data-testid="jsa-submit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Submit JSA to Admin
            </DialogTitle>
            <p className="text-sm text-gray-600">
              Your printed name and signature are required before this JSA is submitted to the admin.
            </p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Your Printed Name *</Label>
              <Input
                value={submitPrintedName}
                onChange={(e) => setSubmitPrintedName(e.target.value)}
                placeholder="Type your full name"
                data-testid="jsa-submit-printed-name"
              />
            </div>
            <div>
              <Label>Your Signature *</Label>
              <SignaturePad value={submitSignature} onChange={setSubmitSignature} height={180} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)} disabled={submittingToAdmin}>Cancel</Button>
            <Button onClick={confirmSubmitToAdmin} disabled={submittingToAdmin} className="bg-green-600 hover:bg-green-700" data-testid="jsa-submit-confirm">
              {submittingToAdmin ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>) : 'Submit to Admin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Templates Dialog */}
      <Dialog open={showTemplatesDialog} onOpenChange={setShowTemplatesDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('jsa.myTemplates')}</DialogTitle>
            <p className="text-sm text-gray-600">Reusable templates for common tasks</p>
          </DialogHeader>

          <div className="space-y-3">
            {taskTemplates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                <p>{t('jsa.noTemplatesSaved')}</p>
                <p className="text-sm">Create a JSA and save it as a template to reuse later</p>
              </div>
            ) : (
              taskTemplates.map((template) => (
                <Card key={template.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{template.taskName}</h3>
                        {template.description && (
                          <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                        )}
                        <div className="mt-2 space-y-1">
                          <p className="text-xs text-gray-500">
                            {template.hazards.length} hazard{template.hazards.length !== 1 ? 's' : ''}
                          </p>
                          <p className="text-xs text-gray-500">
                            Used {template.usageCount} time{template.usageCount !== 1 ? 's' : ''}
                          </p>
                          <p className="text-xs text-gray-500">
                            Created by {template.createdByName}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          onClick={() => handleLoadTemplate(template.id)}
                        >
                          Load
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteTemplate(template.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowTemplatesDialog(false)}>{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save as Template Dialog */}
      <Dialog open={showSaveTemplateDialog} onOpenChange={setShowSaveTemplateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('jsa.saveAsTemplate')}</DialogTitle>
            <p className="text-sm text-gray-600">
              Reuse this task with its hazards for future JSAs
            </p>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('jsa.templateNameLabel')}</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Standard Excavation Process"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('jsa.templateDescLabel')}</Label>
              <Textarea
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Additional notes about this template..."
                rows={2}
              />
            </div>

            <div className="bg-gray-50 p-3 rounded">
              <p className="text-sm font-semibold mb-2">
                This template will include {formData.hazards.length} hazard{formData.hazards.length !== 1 ? 's' : ''}:
              </p>
              <ul className="text-xs space-y-1 text-gray-600">
                {formData.hazards.slice(0, 3).map((h, idx) => (
                  <li key={idx}>• {h.hazard}</li>
                ))}
                {formData.hazards.length > 3 && (
                  <li>... and {formData.hazards.length - 3} more</li>
                )}
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveTemplateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAsTemplate}>
              <FileText className="mr-2 h-4 w-4" />
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import from JHA Dialog */}
      <Dialog open={showJhaImportDialog} onOpenChange={setShowJhaImportDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              Import from Pre-Construction JHA Library
            </DialogTitle>
            <p className="text-sm text-gray-600">
              Import tasks and hazards from approved Pre-Construction JHAs
            </p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="mb-4">
              <Input
                placeholder="Search tasks, projects, or hazards..."
                value={jhaImportSearch}
                onChange={(e) => {
                  setJhaImportSearch(e.target.value);
                  // Debounce search
                  clearTimeout(window.jhaSearchTimeout);
                  window.jhaSearchTimeout = setTimeout(() => {
                    fetchJhaImportTasks(e.target.value);
                  }, 300);
                }}
              />
            </div>

            {loadingJhaImport ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                <span className="ml-2">Loading JHA library...</span>
              </div>
            ) : jhaImportTasks.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Sparkles className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No approved JHAs found</p>
                <p className="text-sm">Create and approve Pre-Construction JHAs to populate this library</p>
              </div>
            ) : (
              <div className="space-y-3">
                {jhaImportTasks.map((task, idx) => (
                  <Card key={idx} className="hover:border-purple-300 transition-colors cursor-pointer" onClick={() => handleImportFromJha(task)}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold text-purple-700">{task.task}</h4>
                          <p className="text-xs text-gray-500 mt-1">
                            From: {task.project_name} • {task.project_location}
                          </p>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {task.hazards.slice(0, 3).map((h, i) => (
                              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-orange-100 text-orange-700">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {h.hazard.substring(0, 40)}{h.hazard.length > 40 ? '...' : ''}
                              </span>
                            ))}
                            {task.hazards.length > 3 && (
                              <span className="text-xs text-gray-500">+{task.hazards.length - 3} more</span>
                            )}
                          </div>
                        </div>
                        <Button size="sm" variant="outline" className="ml-4 border-purple-300 text-purple-600">
                          Import
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowJhaImportDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JSAManager;
